import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import NotificationBell from '../components/NotificationBell';
import SendMessageModal from '../components/SendMessageModal';
import ProfileEditor from '../components/ProfileEditor';
import CandidateProfileView from '../components/CandidateProfileView';
import { calculateProfileCompletion } from '../utils/profileCompletion';
import {
  Users,
  UserPlus,
  Search,
  Filter,
  X,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Mail,
  Shield,
  Briefcase,
  GraduationCap,
  Settings,
  LogOut,
  LogIn,
  Eye,
  EyeOff,
  FileText,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Send,
  Check,
  ShoppingCart,
  Key,
  AlertCircle,
  Bell,
  UserCheck,
  UserX,
  Download,
  User,
  Calendar,
  Lock,
  ArrowLeft,
  Plus,
  BadgeCheck,
  Phone,
  Award,
  Languages,
  Database,
  RefreshCw,
  Video,


} from 'lucide-react';
import ScheduleInterviewModal from '../components/ScheduleInterviewModal';

const SidebarItem = ({ icon: Icon, label, active, onClick, badge, hasSubmenu, isOpen }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-4 py-3 mb-2 rounded-xl transition-all duration-200 group ${active
      ? 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 text-emerald-600 shadow-sm border border-emerald-100'
      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }`}
  >
    <div className="flex items-center space-x-3">
      <div className={`p-2 rounded-lg transition-colors ${active ? 'bg-emerald-500 text-white' : 'bg-gray-100/50 text-gray-400 group-hover:bg-white group-hover:text-emerald-500 group-hover:shadow-sm'}`}>
        <Icon size={20} />
      </div>
      <span className={`font-medium ${active ? 'font-semibold' : ''}`}>{label}</span>
    </div>
    {hasSubmenu && (
      <ChevronRight size={16} className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
    )}
    {badge && (
      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm shadow-red-500/20">
        {badge}
      </span>
    )}
  </button>
);

const AdminDashboard = () => {
  const { user: currentUser, logout, impersonate } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/approvals')) return 'approvals';
    if (path.includes('/recruiter-visibility')) return 'recruiter-visibility';
    if (path.includes('/recruiter-requests')) return 'recruiter-requests';
    if (path.includes('/recruiter-interests')) return 'recruiter-interests';
    if (path.includes('/recruiter-feedbacks')) return 'recruiter-feedbacks';
    if (path.includes('/sourcing-partners')) return 'sourcing-partners';
    if (path.includes('/sourcing-feedbacks')) return 'sourcing-feedbacks';
    if (path.includes('/notifications')) return 'notifications';
    if (path.includes('/messages')) return 'messages';
    if (path.includes('/profile/')) return 'profile';
    return 'overview';
  };
  const activeTab = getActiveTab();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [approvalStatus, setApprovalStatus] = useState('pending');
  const [approvalsMenuOpen, setApprovalsMenuOpen] = useState(true);
  const [interviews, setInterviews] = useState([]);
  const [showScheduleInterviewModal, setShowScheduleInterviewModal] = useState(false);
  const [interviewCandidate, setInterviewCandidate] = useState(null);

  // Load active approval tab from localStorage, default to 'pending'
  const [activeApprovalTab, setActiveApprovalTab] = useState(() => {
    const savedApprovalTab = localStorage.getItem('adminDashboard_activeApprovalTab');
    return savedApprovalTab || 'pending';
  });

  // Redirect to overview if on base admin path
  useEffect(() => {
    if (location.pathname === '/admin' || location.pathname === '/admin/') {
      navigate('/admin/overview', { replace: true });
    }
  }, [location.pathname, navigate]);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    candidates: 0,
    recruiters: 0,
    staff: 0,
    admins: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  // Feedback modal is used for viewing Partner Feedbacks & Requirements (not for sending to candidates)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordResetUserId, setPasswordResetUserId] = useState(null);

  const [messageRecipientId, setMessageRecipientId] = useState(null);
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'candidate',
    approval_status: 'approved',
    profile_status: 'incomplete'
  });

  // Recruiter visibility state
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [recruiterRequests, setRecruiterRequests] = useState([]);
  const [visibleCandidates, setVisibleCandidates] = useState([]);
  const [allCandidatesForVisibility, setAllCandidatesForVisibility] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestCandidates, setShowRequestCandidates] = useState(false);
  const [selectedCandidatesCart, setSelectedCandidatesCart] = useState([]);
  const [approvedCandidatesForRequest, setApprovedCandidatesForRequest] = useState([]);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [selectedOccupationFilter, setSelectedOccupationFilter] = useState('all');
  const [candidateStatistics, setCandidateStatistics] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);

  // Recruiter interests state
  const [recruiterInterests, setRecruiterInterests] = useState([]);
  const [interestStatusFilter, setInterestStatusFilter] = useState('pending');
  const [interestTypeFilter, setInterestTypeFilter] = useState('all');

  // Sourcing partners state
  const [sourcingCandidates, setSourcingCandidates] = useState([]);
  const [sourcingStatusFilter, setSourcingStatusFilter] = useState('all');
  const [sourcingPartners, setSourcingPartners] = useState([]);
  const [selectedSourcingPartner, setSelectedSourcingPartner] = useState(null);
  const [showPartnerCandidates, setShowPartnerCandidates] = useState(false);
  const [partnerFeedbacks, setPartnerFeedbacks] = useState([]);
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState('all');
  const [feedbackPartnerTypeFilter, setFeedbackPartnerTypeFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackResponses, setFeedbackResponses] = useState([]);
  const [responseText, setResponseText] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  const [notificationDateSort, setNotificationDateSort] = useState('desc'); // 'asc' | 'desc'
  const [notificationStatusFilter, setNotificationStatusFilter] = useState('all'); // all | unread | read
  const [editUserData, setEditUserData] = useState({});
  const [manualPools, setManualPools] = useState([]);
  const [loadingPools, setLoadingPools] = useState(false);
  const [showCreatePoolModal, setShowCreatePoolModal] = useState(false);
  const [newPoolName, setNewPoolName] = useState('');
  const [selectedPool, setSelectedPool] = useState(null);
  const [showPoolCandidatesModal, setShowPoolCandidatesModal] = useState(false);
  const [poolCandidates, setPoolCandidates] = useState([]);
  const [showAddToPoolModal, setShowAddToPoolModal] = useState(false);
  const [candidateToAddToPool, setCandidateToAddToPool] = useState(null);

  // Reject Request Modal state
  const [showRejectRequestModal, setShowRejectRequestModal] = useState(false);
  const [requestToReject, setRequestToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingRequest, setRejectingRequest] = useState(false);

  // New state for adding candidate TO a pool (reverse flow)
  const [showSelectCandidateModal, setShowSelectCandidateModal] = useState(false);
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');

  // Occupation statistics state
  const [showOccupationModal, setShowOccupationModal] = useState(false);
  const [selectedOccupationStats, setSelectedOccupationStats] = useState(null);
  const [occupationCandidates, setOccupationCandidates] = useState([]);

  // Helper function to get the correct photo URL (removes /api from base URL)
  const getPhotoUrl = (photoUrl, updatedAt) => {
    if (!photoUrl) return null;

    // Get base URL - use environment variable or construct from current origin
    let baseUrl = process.env.REACT_APP_API_URL;

    // If no env var, construct from current window location
    if (!baseUrl) {
      const origin = window.location.origin;
      // Replace port 3000 with 8080 for backend
      baseUrl = origin.replace(':3000', ':8080');
    }

    // Remove /api from the end or middle of the URL
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4);
    } else if (baseUrl.includes('/api/')) {
      baseUrl = baseUrl.replace('/api/', '/');
    } else if (baseUrl.endsWith('/api/')) {
      baseUrl = baseUrl.slice(0, -5);
    }

    // Ensure baseUrl doesn't end with /
    baseUrl = baseUrl.replace(/\/$/, '');

    // Ensure photoUrl starts with /
    const normalizedPhotoUrl = photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`;

    // Add cache-busting timestamp ONLY if updatedAt is provided
    let fullUrl = `${baseUrl}${normalizedPhotoUrl}`;

    if (updatedAt) {
      // Use the update timestamp for stable cache busting
      // This ensures flickering only happens when the image actually changes
      const timestamp = new Date(updatedAt).getTime();
      fullUrl += `?t=${timestamp}`;
    }
    // If no timestamp provided, we don't add one, allowing browser caching

    return fullUrl;
  };

  // Fetch all users for statistics (without filters)
  const fetchAllUsersForStats = useCallback(async () => {
    try {
      const response = await api.get('/admin/users');
      console.log('[AdminDashboard] Fetched all users for stats:', response.data);

      let allUsers = [];
      if (Array.isArray(response.data)) {
        allUsers = response.data;
      } else if (response.data?.users) {
        allUsers = response.data.users;
      }

      console.log('[AdminDashboard] Total users for stats:', allUsers.length);

      // Calculate stats from all users
      setStats({
        pending: allUsers.filter(u => u.approval_status === 'pending' && u.role !== 'staff' && u.role !== 'admin').length,
        approved: allUsers.filter(u => u.approval_status === 'approved' && u.role !== 'staff' && u.role !== 'admin').length,
        rejected: allUsers.filter(u => u.approval_status === 'rejected' && u.role !== 'staff' && u.role !== 'admin').length,
        candidates: allUsers.filter(u => u.role === 'candidate').length,
        recruiters: allUsers.filter(u => u.role === 'recruiting_partner').length,
        staff: allUsers.filter(u => u.role === 'staff').length,
        admins: allUsers.filter(u => u.role === 'admin').length
      });
    } catch (error) {
      console.error('[AdminDashboard] Error fetching all users for stats:', error);
      setStats({
        pending: 0,
        approved: 0,
        rejected: 0,
        candidates: 0,
        recruiters: 0,
        staff: 0,
        admins: 0
      });
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    // Only set loading for tabs that actually need user list
    if (activeTab === 'approvals') {
      setLoading(true);
    }
    try {
      const params = {};
      if (activeTab === 'approvals') {
        if (activeApprovalTab === 'pending') {
          params.approval_status = 'pending';
        } else if (activeApprovalTab === 'approved') {
          params.approval_status = 'approved';
        } else if (activeApprovalTab === 'rejected') {
          params.approval_status = 'rejected';
        }
      }
      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }

      const queryString = new URLSearchParams(params).toString();
      const url = `/admin/users${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(url);
      console.log('[AdminDashboard] Fetched users:', response.data);
      // Handle both array and object responses
      let allUsers = [];
      if (Array.isArray(response.data)) {
        allUsers = response.data;
        setUsers(response.data);
      } else if (response.data?.users) {
        allUsers = response.data.users;
        setUsers(response.data.users);
      } else if (response.data && typeof response.data === 'object') {
        // If response.data is an object but doesn't have 'users' key, try to extract users
        console.warn('[AdminDashboard] Unexpected response format:', response.data);
        setUsers([]);
      } else {
        setUsers([]);
      }

      console.log('[AdminDashboard] Processed users count:', allUsers.length);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching users:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('[AdminDashboard] Error details:', errorMessage);
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      if (activeTab === 'approvals') {
        setLoading(false);
      }
    }
  }, [activeTab, activeApprovalTab, roleFilter]);

  const fetchInterviews = useCallback(async () => {
    try {
      const response = await api.get('/api/interviews');
      setInterviews(response.data || []);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      showToast('Failed to fetch interviews');
    }
  }, [showToast]);

  const handleScheduleInterview = async (data) => {
    try {
      await api.post('/api/interviews', data);
      showToast('Interview scheduled successfully');
      setShowScheduleInterviewModal(false);
      fetchInterviews();
    } catch (error) {
      console.error('Error scheduling interview:', error);
      showToast(error.response?.data?.error || 'Failed to schedule interview');
    }
  };


  // Persist activeTab and activeApprovalTab to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('adminDashboard_activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('adminDashboard_activeApprovalTab', activeApprovalTab);
  }, [activeApprovalTab]);

  // Fetch data based on active tab - runs on mount and when tab changes
  useEffect(() => {
    // Use setTimeout to ensure this runs after initial render
    const timer = setTimeout(() => {
      try {
        if (activeTab === 'overview') {
          // Overview tab - fetch stats
          fetchAllUsersForStats();
          fetchCandidateStatistics();
          fetchManualPools();
          fetchUsers(); // Fix: Fetch users so they are available for manual pool candidate selection
        } else if (activeTab === 'approvals' || activeTab === 'all-users') {
          fetchUsers();
        } else if (activeTab === 'interviews') {
          fetchInterviews();
        } else if (activeTab === 'approvals') {
          fetchUsers();
        } else if (activeTab === 'recruiter-visibility') {
          fetchRecruiters();
          fetchAllCandidates();
          fetchCandidateStatistics(); // Fetch statistics for the Candidate Pool by Occupation section
          // Reset request candidates view when switching to visibility tab
          setShowRequestCandidates(false);
          setSelectedRequest(null);
        } else if (activeTab === 'recruiter-requests') {
          fetchRecruiterRequests();
        } else if (activeTab === 'recruiter-interests') {
          fetchRecruiterInterests(interestStatusFilter, interestTypeFilter);
        } else if (activeTab === 'notifications') {
          fetchNotifications();
        } else if (activeTab === 'sourcing-partners') {
          fetchSourcingPartners();
          // Always fetch all candidates when showing the "All Candidates per sourcing partner" view
          if (!showPartnerCandidates) {
            fetchSourcingCandidates();
          }
        } else if (activeTab === 'partner-feedbacks' || activeTab === 'recruiter-feedbacks' || activeTab === 'sourcing-feedbacks') {
          fetchPartnerFeedbacks();
        }
      } catch (error) {
        console.error('[AdminDashboard] Error in useEffect:', error);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [activeTab, activeApprovalTab, roleFilter, showPartnerCandidates]);

  // Check URL params for pre-selected recruiter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const recruiterId = urlParams.get('recruiterId');
    const tab = urlParams.get('tab');
    if (recruiterId && tab === 'recruiter-visibility') {
      navigate('/admin/partner-feedbacks');
      // Will be set after recruiters are fetched
    }
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/admin/notifications');
      const notifs = Array.isArray(response.data) ? response.data : [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    }
  };



  const fetchSourcingCandidates = async () => {
    try {
      const params = new URLSearchParams();
      if (sourcingStatusFilter && sourcingStatusFilter !== 'all') {
        params.append('status', sourcingStatusFilter);
      }
      const url = `/admin/sourcing-candidates${params.toString() ? `?${params.toString()}` : ''}`;
      console.log('[AdminDashboard] Fetching sourcing candidates from:', url);
      const response = await api.get(url);
      console.log('[AdminDashboard] Fetched sourcing candidates:', response.data);
      console.log('[AdminDashboard] Number of candidates:', Array.isArray(response.data) ? response.data.length : 'not an array');

      // Ensure we're setting an array
      if (Array.isArray(response.data)) {
        setSourcingCandidates(response.data);
      } else if (response.data && typeof response.data === 'object') {
        // If response.data is an object, try to extract an array from it
        console.warn('[AdminDashboard] Response data is not an array:', response.data);
        setSourcingCandidates([]);
      } else {
        setSourcingCandidates([]);
      }
    } catch (error) {
      console.error('[AdminDashboard] Error fetching sourcing candidates:', error);
      console.error('[AdminDashboard] Error response:', error.response);
      console.error('[AdminDashboard] Error details:', error.response?.data);
      console.error('[AdminDashboard] Error status:', error.response?.status);
      setSourcingCandidates([]);
    }
  };

  const fetchSourcingPartners = async () => {
    try {
      const response = await api.get('/admin/sourcing-partners');
      setSourcingPartners(response.data || []);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching sourcing partners:', error);
      setSourcingPartners([]);
    }
  };

  const fetchPartnerCandidates = async (partnerId) => {
    try {
      const response = await api.get(`/admin/sourcing-partners/${partnerId}/candidates`);
      setSourcingCandidates(response.data || []);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching partner candidates:', error);
      setSourcingCandidates([]);
    }
  };

  const handleSelectSourcingPartner = async (partner) => {
    setSelectedSourcingPartner(partner);
    setShowPartnerCandidates(true);
    await fetchPartnerCandidates(partner.id);
  };

  const handleViewAllCandidates = async () => {
    setSelectedSourcingPartner(null);
    setShowPartnerCandidates(false);
    await fetchSourcingCandidates();
  };

  const fetchPartnerFeedbacks = async () => {
    try {
      const params = new URLSearchParams();
      if (feedbackStatusFilter !== 'all') params.append('status', feedbackStatusFilter);
      if (feedbackPartnerTypeFilter !== 'all') params.append('partner_type', feedbackPartnerTypeFilter);

      const response = await api.get(`/admin/partner-feedbacks?${params.toString()}`);
      setPartnerFeedbacks(response.data || []);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching partner feedbacks:', error);
      setPartnerFeedbacks([]);
    }
  };

  const fetchFeedbackResponses = async (feedbackId) => {
    try {
      const response = await api.get(`/admin/partner-feedbacks/${feedbackId}/responses`);
      setFeedbackResponses(response.data || []);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching feedback responses:', error);
      setFeedbackResponses([]);
    }
  };

  const handleViewFeedback = async (feedback) => {
    setSelectedFeedback(feedback);
    setShowFeedbackModal(true);
    await fetchFeedbackResponses(feedback.id);

    // Mark as read if it's new
    if (feedback.status === 'new') {
      try {
        await api.put(`/admin/partner-feedbacks/${feedback.id}/status`, { status: 'read' });
        await fetchPartnerFeedbacks();
      } catch (error) {
        console.error('Error marking feedback as read:', error);
      }
    }
  };

  const handleRespondToFeedback = async () => {
    if (!responseText.trim()) {
      showToast('Please enter a response');
      return;
    }

    try {
      await api.post(`/admin/partner-feedbacks/${selectedFeedback.id}/respond`, {
        response_text: responseText
      });
      setResponseText('');
      await fetchFeedbackResponses(selectedFeedback.id);
      await fetchPartnerFeedbacks();
    } catch (error) {
      console.error('Error submitting response:', error);
      showToast('Failed to submit response. Please try again.');
    }
  };

  const handleUpdateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      await api.put(`/admin/partner-feedbacks/${feedbackId}/status`, { status: newStatus });
      await fetchPartnerFeedbacks();
      if (selectedFeedback && selectedFeedback.id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating feedback status:', error);
      showToast('Failed to update status. Please try again.');
    }
  };

  const handleApproveInterest = async (notification, recruiterList = null, fetchRecruitersFn = null) => {
    try {
      // Parse notification message to extract recruiter email and candidate info
      // Message format: "Recruiting Partner/Employer {name} ({email}) has shown interest in candidate {name} ({email})"
      const message = notification.message;
      const recruiterMatch = message.match(/(?:Recruiting Partner|Employer) (.+?) \((.+?)\)/);
      const candidateMatch = message.match(/candidate (.+?) \((.+?)\)/);

      if (!recruiterMatch || !candidateMatch || !notification.reference_id) {
        showToast('Could not parse notification details');
        return false;
      }

      const recruiterEmail = recruiterMatch[2];
      const candidateId = notification.reference_id;

      // Use provided recruiter list or fetch if needed
      let recruiterListToUse = recruiterList || recruiters;
      if (recruiterListToUse.length === 0) {
        if (fetchRecruitersFn) {
          await fetchRecruitersFn();
          recruiterListToUse = recruiters;
        } else {
          await fetchRecruiters();
          recruiterListToUse = recruiters;
        }
      }

      const recruiter = recruiterListToUse.find(r => r.email === recruiterEmail);
      if (!recruiter) {
        showToast('Recruiter not found. Please try again.');
        return false;
      }

      // Grant visibility
      await api.post(`/admin/recruiters/${recruiter.id}/candidates`, {
        candidate_ids: [candidateId]
      });

      // Refresh notifications and requests
      if (activeTab === 'notifications') {
        await fetchNotifications();
      }
      await fetchRecruiterRequests();
      // Success - no alert needed, UI will update automatically
      return true;
    } catch (error) {
      console.error('[AdminDashboard] Error approving interest:', error);
      showToast('Failed to approve interest');
      return false;
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.put(`/admin/notifications/${notificationId}/read`);
      await fetchNotifications();
    } catch (error) {
      console.error('[AdminDashboard] Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.type === 'recruiter_interest') {
      navigate('/admin/recruiter-visibility');
      // The recruiter will be selected when fetchRecruiters runs
    } else if (notification.type === 'recruiter_request') {
      navigate('/admin/recruiter-requests');
    } else if (notification.type === 'partner_feedback') {
      navigate('/admin/partner-feedbacks');
      await fetchPartnerFeedbacks();
    } else if (notification.type === 'profile_message' || notification.type === 'candidate_feedback') {
      // Open message modal if reference_id is available
      if (notification.reference_id) {
        try {
          const userResponse = await api.get(`/admin/users/${notification.reference_id}`);
          setMessageRecipient(userResponse.data);
          setShowMessageModal(true);
        } catch (error) {
          console.error('Error fetching user for notification:', error);
        }
      }
    }
  };

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, activeApprovalTab, activeTab]);

  const filterUsers = () => {
    if (!users || users.length === 0) {
      console.log('[AdminDashboard] filterUsers: No users to filter');
      setFilteredUsers([]);
      return;
    }

    let filtered = [...users];
    console.log('[AdminDashboard] filterUsers: Starting with', filtered.length, 'users');

    if (activeTab === 'approvals') {
      if (activeApprovalTab === 'pending') {
        filtered = filtered.filter(u => u.approval_status === 'pending');
        console.log('[AdminDashboard] filterUsers: After pending filter:', filtered.length);
      } else if (activeApprovalTab === 'approved') {
        filtered = filtered.filter(u => u.approval_status === 'approved');
        console.log('[AdminDashboard] filterUsers: After approved filter:', filtered.length);
      } else if (activeApprovalTab === 'rejected') {
        filtered = filtered.filter(u => u.approval_status === 'rejected');
        console.log('[AdminDashboard] filterUsers: After rejected filter:', filtered.length);
      }
    }

    if (roleFilter !== 'all') {
      const beforeRole = filtered.length;
      filtered = filtered.filter(u => u.role === roleFilter);
      console.log('[AdminDashboard] filterUsers: After role filter (' + roleFilter + '):', filtered.length, '(was', beforeRole + ')');
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const beforeSearch = filtered.length;
      filtered = filtered.filter(u =>
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.role?.toLowerCase().includes(term)
      );
      console.log('[AdminDashboard] filterUsers: After search filter:', filtered.length, '(was', beforeSearch + ')');
    }

    console.log('[AdminDashboard] filterUsers: Final filtered count:', filtered.length);
    setFilteredUsers(filtered);
  };

  const fetchManualPools = useCallback(async () => {
    setLoadingPools(true);
    try {
      const response = await api.get('/admin/manual-pools');
      setManualPools(response.data.pools || []);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching manual pools:', error);
    } finally {
      setLoadingPools(false);
    }
  }, []);

  const handleCreatePool = async () => {
    if (!newPoolName.trim()) return;
    try {
      await api.post('/admin/manual-pools', { name: newPoolName });
      setNewPoolName('');
      setShowCreatePoolModal(false);
      fetchManualPools();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to create pool');
    }
  };

  const handleDeletePool = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pool?')) return;
    try {
      await api.delete(`/admin/manual-pools/${id}`);
      fetchManualPools();
    } catch (error) {
      showToast('Failed to delete pool');
    }
  };

  const handleAddCandidateToPool = async (candidate) => {
    if (!selectedPool || !candidate) return;
    try {
      await api.post(`/admin/manual-pools/${selectedPool.id}/candidates`, {
        candidate_ids: [candidate.id]
      });
      fetchPoolCandidates(selectedPool);
      fetchManualPools();
      setShowSelectCandidateModal(false);
    } catch (error) {
      console.error(error);
      showToast('Failed to add candidate: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const fetchPoolCandidates = async (pool) => {
    setSelectedPool(pool);
    setLoadingPools(true);
    try {
      const response = await api.get(`/admin/manual-pools/${pool.id}/candidates`);
      setPoolCandidates(response.data.candidates || []);
      setShowPoolCandidatesModal(true);
    } catch (error) {
      console.error('Error fetching pool candidates:', error);
    } finally {
      setLoadingPools(false);
    }
  };

  const handleAddToPool = async (poolId) => {
    if (!candidateToAddToPool) return;
    try {
      await api.post(`/admin/manual-pools/${poolId}/candidates`, {
        candidate_ids: [candidateToAddToPool.id]
      });
      setShowAddToPoolModal(false);
      setCandidateToAddToPool(null);
      fetchManualPools();
      showToast('Candidate added to pool successfully');
    } catch (error) {
      showToast('Failed to add candidate to pool');
    }
  };

  const handleRemoveFromPool = async (candidateId) => {
    if (!selectedPool) return;
    try {
      await api.delete(`/admin/manual-pools/${selectedPool.id}/candidates/${candidateId}`);
      // Refresh candidates list
      const response = await api.get(`/admin/manual-pools/${selectedPool.id}/candidates`);
      setPoolCandidates(response.data.candidates || []);
      fetchManualPools();
    } catch (error) {
      showToast('Failed to remove candidate');
    }
  };

  const handleApprove = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/approval`, { status: 'approved' });
      await fetchUsers();
    } catch (error) {
      console.error('[AdminDashboard] Error approving user:', error);
      showToast('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/approval`, { status: 'rejected' });
      await fetchUsers();
    } catch (error) {
      console.error('[AdminDashboard] Error rejecting user:', error);
      showToast('Failed to reject user');
    }
  };

  const handleUpdateStatus = async (userId, status) => {
    try {
      await api.put(`/admin/users/${userId}/approval`, { status });
      await fetchUsers();
    } catch (error) {
      console.error('[AdminDashboard] Error updating user status:', error);
      showToast(`Failed to update user status to ${status}`);
    }
  };

  const handleDelete = async (userId) => {
    // Prevent deletion of the main admin user
    const userToDelete = users.find(u => u.id === userId);
    if (userToDelete && userToDelete.email === 'admin@polygotstalent.com') {
      showToast('Cannot delete the main admin user (admin@polygotstalent.com)');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    try {
      await api.delete(`/admin/users/${userId}`);
      await fetchUsers();
    } catch (error) {
      console.error('[AdminDashboard] Error deleting user:', error);
      showToast('Failed to delete user');
    }
  };

  const handleImpersonate = async (userToImpersonate) => {
    try {
      if (userToImpersonate.role === 'admin') {
        showToast('Cannot impersonate another admin', 'error');
        return;
      }

      const { success, error } = await impersonate(userToImpersonate.id);
      if (success) {
        showToast(`Impersonating ${userToImpersonate.name} as ${userToImpersonate.role.replace('_', ' ')}`, 'success');
        switch (userToImpersonate.role) {
          case 'staff': navigate('/staff'); break;
          case 'candidate': navigate('/student'); break;
          case 'recruiting_partner': navigate('/recruiting'); break;
          case 'sourcing_partner': navigate('/sourcing'); break;
          case 'employer': navigate('/employer'); break;
          default: navigate('/');
        }
      } else {
        showToast(error || 'Failed to impersonate user', 'error');
      }
    } catch (err) {
      console.error('Impersonation error:', err);
      showToast('An error occurred during impersonation', 'error');
    }
  };

  const handleViewUser = async (user) => {
    setUserDetailsLoading(true);
    setSelectedUser(user);
    // Navigate to profile page with full page reload
    window.location.href = `/admin/profile/${user.id}`;
    setUserDetailsLoading(false);
  };

  // If this is a sourcing partner candidate, mark it as viewed
  const markCandidateAsViewed = async (user) => {
    if (user.sourcing_partner_id && activeTab === 'sourcing-partners') {
      try {
        await api.put(`/admin/sourcing-candidates/${user.id}/viewed`);
        // Update local state to remove "new" badge
        setSourcingCandidates(prev => prev.map(c =>
          c.id === user.id ? { ...c, is_new: false } : c
        ));
      } catch (error) {
        console.error('Error marking candidate as viewed:', error);
      }
    }
  };

  const handleEditUser = async (userData, shouldClose = true) => {
    try {
      // If userData is provided (from manual call), update via API
      // If userData is missing (callback from ProfileEditor which handles update internally), skip API call
      if (userData) {
        await api.put(`/admin/users/${selectedUser.id}`, userData);
      }

      await fetchUsers();
      await fetchAllUsersForStats(); // Refresh stats after edit

      if (shouldClose) {
        setShowUserModal(false);
        setShowEditModal(false);
      }
    } catch (error) {
      console.error('[AdminDashboard] Error updating user:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to update user';
      showToast(`Failed to update user: ${errorMessage}`);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-5 w-5 text-green-600" />;
      case 'staff':
        return <Users className="h-5 w-5 text-green-600" />;
      case 'recruiting_partner':
        return <Briefcase className="h-5 w-5 text-purple-600" />;
      case 'sourcing_partner':
        return <Users className="h-5 w-5 text-orange-500" />;
      case 'candidate':
        return <GraduationCap className="h-5 w-5 text-orange-600" />;
      default:
        return <Users className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            Approved
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
            Rejected
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    try {
      if (passwordResetUserId) {
        // Reset password for another user (admin only)
        await api.post(`/admin/users/${passwordResetUserId}/reset-password`, {
          password: newPassword
        });
        // showToast('User password reset successfully'); // Removed success alert
      } else {
        // Reset own password
        await api.post('/auth/reset-password', {
          password: newPassword,
          token: localStorage.getItem('token')
        });
        // showToast('Password reset successfully'); // Removed success alert
      }
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordResetUserId(null);
    } catch (error) {
      console.error('[AdminDashboard] Error resetting password:', error);
      setPasswordError(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Recruiter visibility functions
  const fetchRecruiters = async () => {
    try {
      const response = await api.get('/admin/users?role=recruiting_partner');
      let recruitersList = [];
      if (Array.isArray(response.data)) {
        recruitersList = response.data;
      } else if (response.data?.users) {
        recruitersList = response.data.users;
      }
      setRecruiters(recruitersList);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching recruiters:', error);
      setRecruiters([]);
    }
  };

  const fetchAllCandidates = async () => {
    try {
      // Fetch all candidates (both approved and pending) so admin can approve them for recruiters
      const response = await api.get('/admin/users?role=candidate');
      console.log('[AdminDashboard] Fetched all candidates:', response.data);
      // Handle both array and object responses
      let candidatesList = [];
      if (Array.isArray(response.data)) {
        candidatesList = response.data;
      } else if (response.data?.users) {
        candidatesList = response.data.users;
      }
      // Filter to only show approved candidates (admin can only grant visibility to approved candidates)
      const approvedCandidates = candidatesList.filter(c => c.approval_status === 'approved');
      console.log('[AdminDashboard] Approved candidates count:', approvedCandidates.length);
      setAllCandidatesForVisibility(approvedCandidates);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching candidates:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Error details:', errorMessage);
      setAllCandidatesForVisibility([]);
    }
  };

  const fetchVisibleCandidates = async (recruiterId) => {
    try {
      const response = await api.get(`/admin/recruiters/${recruiterId}/candidates`);
      console.log('[AdminDashboard] Fetched visible candidates response:', response.data);
      // Handle both array and object responses
      let candidatesList = [];
      if (Array.isArray(response.data)) {
        candidatesList = response.data;
      } else if (response.data?.candidates) {
        candidatesList = response.data.candidates;
      }
      console.log('[AdminDashboard] Visible candidates count:', candidatesList.length);
      setVisibleCandidates(candidatesList);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching visible candidates:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Error details:', errorMessage);
      setVisibleCandidates([]);
    }
  };

  const fetchRecruiterRequests = async () => {
    try {
      const response = await api.get('/admin/recruiter-requests');
      console.log('[AdminDashboard] Fetched recruiter requests:', response.data);
      setRecruiterRequests(response.data.requests || []);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching recruiter requests:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Error details:', errorMessage);
      setRecruiterRequests([]);
    }
  };

  const fetchRecruiterInterests = async (statusFilter = null, typeFilter = null) => {
    try {
      // Don't set main loading state - this is a secondary data fetch
      const params = new URLSearchParams();
      const status = statusFilter !== null ? statusFilter : interestStatusFilter;
      const type = typeFilter !== null ? typeFilter : interestTypeFilter;

      if (status !== 'all') {
        params.append('status', status);
      }
      if (type !== 'all') {
        params.append('type', type);
      }
      const url = `/admin/recruiter-interests${params.toString() ? `?${params.toString()}` : ''}`;
      console.log('[AdminDashboard] Fetching recruiter interests from:', url);
      const response = await api.get(url);
      console.log('[AdminDashboard] Fetched recruiter interests response:', response.data);

      // Handle different response structures
      let interests = [];
      if (Array.isArray(response.data)) {
        interests = response.data;
      } else if (response.data?.interests) {
        interests = response.data.interests;
      } else if (response.data?.data) {
        interests = Array.isArray(response.data.data) ? response.data.data : [];
      }

      console.log('[AdminDashboard] Processed interests:', interests.length);
      setRecruiterInterests(interests);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching recruiter interests:', error);
      console.error('[AdminDashboard] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Error details:', errorMessage);
      setRecruiterInterests([]);
      // Don't show alert for every error, just log it
      console.warn('Failed to fetch recruiter interests:', errorMessage);
    }
  };

  const handleAckInterest = async (interestId) => {
    try {
      console.log('[AdminDashboard] Acknowledging interest:', interestId);
      const response = await api.put(`/admin/recruiter-interests/${interestId}/ack`);
      console.log('[AdminDashboard] Ack response:', response.data);

      // If currently filtering by 'pending', switch to 'acked' to show the newly acked item
      const newStatusFilter = interestStatusFilter === 'pending' ? 'acked' : interestStatusFilter;
      if (newStatusFilter !== interestStatusFilter) {
        setInterestStatusFilter(newStatusFilter);
      }

      // Refresh the list with the new filter to show updated status
      await fetchRecruiterInterests(newStatusFilter, interestTypeFilter);

      // No alert - UI will update automatically
    } catch (error) {
      console.error('[AdminDashboard] Error acknowledging interest:', error);
      console.error('[AdminDashboard] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to acknowledge interest';
      showToast(`Failed to acknowledge interest: ${errorMessage}`);
    }
  };

  const handleMarkInterestClosed = async (interestId) => {
    try {
      console.log('[AdminDashboard] Marking interest as closed:', interestId);
      const response = await api.put(`/admin/recruiter-interests/${interestId}/close`);
      console.log('[AdminDashboard] Close response:', response.data);

      // If currently filtering by 'acked', switch to 'closed' to show the newly closed item
      const newStatusFilter = interestStatusFilter === 'acked' ? 'closed' : interestStatusFilter;
      if (newStatusFilter !== interestStatusFilter) {
        setInterestStatusFilter(newStatusFilter);
      }

      // Refresh the list with the new filter to show updated status
      await fetchRecruiterInterests(newStatusFilter, interestTypeFilter);

      // No alert - UI will update automatically
    } catch (error) {
      console.error('[AdminDashboard] Error marking interest as closed:', error);
      console.error('[AdminDashboard] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to mark interest as closed';
      showToast(`Failed to mark interest as closed: ${errorMessage}`);
    }
  };

  const fetchCandidateStatistics = async () => {
    try {
      const response = await api.get('/admin/candidate-statistics');
      console.log('[AdminDashboard] Fetched candidate statistics:', response.data);
      setCandidateStatistics(response.data.statistics || []);
      setTotalCandidates(response.data.total_candidates || 0);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching candidate statistics:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Error details:', errorMessage);
      setCandidateStatistics([]);
      setTotalCandidates(0);
    }
  };

  const handleApproveSourcingCandidate = async (candidateId) => {
    try {
      await api.put(`/admin/sourcing-candidates/${candidateId}/approve`);
      if (showPartnerCandidates && selectedSourcingPartner) {
        await fetchPartnerCandidates(selectedSourcingPartner.id);
      } else {
        await fetchSourcingCandidates();
      }
      await fetchSourcingPartners(); // Refresh partner counts
      // showToast('Candidate approved successfully'); // Removed success alert
    } catch (error) {
      console.error('[AdminDashboard] Error approving sourcing candidate:', error);
      showToast('Failed to approve candidate');
    }
  };

  const handleRejectSourcingCandidate = async (candidateId, reason = '') => {
    try {
      await api.put(`/admin/sourcing-candidates/${candidateId}/reject`, { reason });
      if (showPartnerCandidates && selectedSourcingPartner) {
        await fetchPartnerCandidates(selectedSourcingPartner.id);
      } else {
        await fetchSourcingCandidates();
      }
      await fetchSourcingPartners(); // Refresh partner counts
      // showToast('Candidate rejected successfully'); // Removed success alert
    } catch (error) {
      console.error('[AdminDashboard] Error rejecting sourcing candidate:', error);
      showToast('Failed to reject candidate');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!createUserForm.name || !createUserForm.email || !createUserForm.password) {
      showToast('Please fill in all required fields');
      return;
    }

    // Prevent admin from creating another admin user
    if (createUserForm.role === 'admin') {
      showToast('Cannot create another admin user');
      return;
    }

    try {
      await api.post('/admin/users', createUserForm);
      // showToast('User created successfully'); // Removed success alert
      setShowCreateModal(false);
      setCreateUserForm({
        name: '',
        email: '',
        password: '',
        role: 'candidate',
        approval_status: 'approved',
        profile_status: 'incomplete'
      });
      await fetchUsers();
      await fetchAllUsersForStats();
    } catch (error) {
      console.error('[AdminDashboard] Error creating user:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to create user';
      showToast(`Failed to create user: ${errorMessage}`);
    }
  };

  const handleGrantVisibility = async (recruiterId, candidateId) => {
    try {
      await api.post(`/admin/recruiters/${recruiterId}/candidates`, { candidate_ids: [candidateId] });
      await Promise.all([
        fetchVisibleCandidates(recruiterId),
        fetchRecruiterRequests(),
        fetchAllCandidates()
      ]);
      // Success - no alert needed, UI will update automatically
    } catch (error) {
      console.error('[AdminDashboard] Error granting visibility:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to grant visibility';
      showToast(`Failed to grant visibility: ${errorMessage}`);
    }
  };

  const handleRevokeVisibility = async (recruiterId, candidateId) => {
    try {
      await api.delete(`/admin/recruiters/${recruiterId}/candidates/${candidateId}`);
      await Promise.all([
        fetchVisibleCandidates(recruiterId),
        fetchAllCandidates()
      ]);
      // showToast('Visibility revoked successfully'); // Removed success alert
    } catch (error) {
      console.error('[AdminDashboard] Error revoking visibility:', error);
      showToast('Failed to revoke visibility');
    }
  };

  const handleManageCandidates = async (request) => {
    try {
      console.log('[AdminDashboard] handleManageCandidates called with request:', request);
      // Ensure we're on the recruiter-requests tab
      navigate('/admin/recruiter-requests');
      setSelectedRequest(request);
      setShowRequestCandidates(true);

      // Reset visible candidates first
      setVisibleCandidates([]);
      setAllCandidatesForVisibility([]);
      setSelectedCandidatesCart([]);
      setApprovedCandidatesForRequest([]);
      setCandidateSearchTerm('');
      setSelectedOccupationFilter('all');

      // Always fetch fresh data sequentially to ensure proper state updates
      await fetchAllCandidates();
      await fetchRecruiters();

      if (request.recruiter_id) {
        await fetchVisibleCandidates(request.recruiter_id);
        // Load already approved candidates for this request
        try {
          const visible = await api.get(`/admin/recruiters/${request.recruiter_id}/candidates`);
          const visibleList = Array.isArray(visible.data) ? visible.data : (visible.data?.candidates || []);
          // Filter to only show candidates linked to this specific request (if request_id is available)
          const approvedForThisRequest = visibleList.filter(c =>
            !c.request_id || c.request_id === request.id
          );
          setApprovedCandidatesForRequest(approvedForThisRequest);
        } catch (err) {
          console.error('[AdminDashboard] Error loading approved candidates:', err);
        }
      } else {
        console.error('Request missing recruiter_id:', request);
        showToast('Error: Request missing recruiter information');
      }
    } catch (error) {
      console.error('[AdminDashboard] Error in handleManageCandidates:', error);
      showToast('Failed to load candidate management view');
    }
  };

  const handleApproveCandidateForRequest = async (candidateIds) => {
    if (!selectedRequest) {
      console.error('[AdminDashboard] No selected request');
      showToast('Error: No request selected');
      return;
    }

    // Ensure candidateIds is an array
    const idsToApprove = Array.isArray(candidateIds) ? candidateIds : [candidateIds];

    if (idsToApprove.length === 0) {
      showToast('Please select at least one candidate to approve');
      return;
    }

    console.log('[AdminDashboard] Approving candidates', idsToApprove, 'for request', selectedRequest.id, 'recruiter', selectedRequest.recruiter_id);

    try {
      const response = await api.post(`/admin/recruiters/${selectedRequest.recruiter_id}/candidates`, {
        candidate_ids: idsToApprove,
        request_id: selectedRequest.id
      });
      console.log('[AdminDashboard] Approve response:', response.data);

      // Refresh the lists
      await Promise.all([
        fetchAllCandidates(),
        fetchVisibleCandidates(selectedRequest.recruiter_id),
        fetchRecruiterRequests()
      ]);

      // Refresh approved candidates list for this request
      if (selectedRequest.recruiter_id) {
        try {
          const visible = await api.get(`/admin/recruiters/${selectedRequest.recruiter_id}/candidates`);
          const visibleList = Array.isArray(visible.data) ? visible.data : (visible.data?.candidates || []);
          const approvedForThisRequest = visibleList.filter(c =>
            !c.request_id || c.request_id === selectedRequest.id
          );
          setApprovedCandidatesForRequest(approvedForThisRequest);
        } catch (err) {
          console.error('[AdminDashboard] Error refreshing approved candidates:', err);
        }
      }

      // Clear cart
      setSelectedCandidatesCart([]);

      // showToast(`Successfully approved ${idsToApprove.length} candidate(s)`); // Removed success alert
    } catch (error) {
      console.error('[AdminDashboard] Error approving candidates:', error);
      console.error('[AdminDashboard] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to approve candidates';
      showToast(`Failed to approve candidates: ${errorMessage}`);
    }
  };

  const handleRemoveCandidateFromRequest = async (candidateId) => {
    if (!selectedRequest) {
      console.error('[AdminDashboard] No selected request');
      showToast('Error: No request selected');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this candidate from the approved list?')) {
      return;
    }

    console.log('[AdminDashboard] Removing candidate', candidateId, 'from request', selectedRequest.id, 'recruiter', selectedRequest.recruiter_id);

    try {
      await api.delete(`/admin/recruiters/${selectedRequest.recruiter_id}/candidates/${candidateId}`);
      console.log('[AdminDashboard] Candidate removed successfully');

      // Refresh the lists
      await Promise.all([
        fetchAllCandidates(),
        fetchVisibleCandidates(selectedRequest.recruiter_id),
        fetchRecruiterRequests()
      ]);

      // Refresh approved candidates list for this request
      if (selectedRequest.recruiter_id) {
        try {
          const visible = await api.get(`/admin/recruiters/${selectedRequest.recruiter_id}/candidates`);
          const visibleList = Array.isArray(visible.data) ? visible.data : (visible.data?.candidates || []);
          const approvedForThisRequest = visibleList.filter(c =>
            !c.request_id || c.request_id === selectedRequest.id
          );
          setApprovedCandidatesForRequest(approvedForThisRequest);
        } catch (err) {
          console.error('[AdminDashboard] Error refreshing approved candidates:', err);
        }
      }

      // showToast('Candidate removed successfully'); // Removed success alert
    } catch (error) {
      console.error('[AdminDashboard] Error removing candidate:', error);
      console.error('[AdminDashboard] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to remove candidate';
      showToast(`Failed to remove candidate: ${errorMessage}`);
    }
  };

  const toggleCandidateInCart = (candidateId) => {
    setSelectedCandidatesCart(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const getOccupationCategories = () => {
    const occupations = new Set();
    allCandidatesForVisibility.forEach(candidate => {
      if (candidate.occupation) {
        occupations.add(candidate.occupation);
      } else {
        occupations.add('Not Specified');
      }
    });
    return Array.from(occupations).sort();
  };

  const getFilteredCandidates = () => {
    let filtered = allCandidatesForVisibility;

    // Filter by occupation
    if (selectedOccupationFilter !== 'all') {
      if (selectedOccupationFilter === 'Not Specified') {
        filtered = filtered.filter(c => !c.occupation);
      } else {
        filtered = filtered.filter(c => c.occupation === selectedOccupationFilter);
      }
    }

    // Filter by search term
    if (candidateSearchTerm.trim()) {
      const searchLower = candidateSearchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(searchLower) ||
        c.email?.toLowerCase().includes(searchLower) ||
        c.qualification?.toLowerCase().includes(searchLower) ||
        c.occupation?.toLowerCase().includes(searchLower)
      );
    }

    // Exclude already approved candidates for this request
    if (approvedCandidatesForRequest.length > 0) {
      const approvedIds = approvedCandidatesForRequest.map(ac => ac.id);
      filtered = filtered.filter(c => !approvedIds.includes(c.id));
    }
    return filtered;
  };

  const handleOccupationClick = async (occupation) => {
    try {
      if (!occupation) return;

      setShowOccupationModal(true);
      setSelectedOccupationStats(occupation);
      setOccupationCandidates([]);

      // Fetch candidates for this occupation
      const response = await api.get('/admin/users?role=candidate');
      let allCandidates = [];
      if (Array.isArray(response.data)) {
        allCandidates = response.data;
      } else if (response.data?.users) {
        allCandidates = response.data.users;
      }

      // Filter by occupation
      const filtered = allCandidates.filter(c => c.occupation === occupation);
      setOccupationCandidates(filtered);
    } catch (error) {
      console.error('Error fetching occupation details:', error);
    }
  };

  const handleRejectRequest = async () => {
    if (!requestToReject || !rejectReason.trim()) return;

    setRejectingRequest(true);
    try {
      await api.put(`/admin/recruiter-requests/${requestToReject.id}/status`, {
        status: 'rejected',
        admin_comment: rejectReason.trim()
      });

      await fetchRecruiterRequests();

      setShowRejectRequestModal(false);
      setRequestToReject(null);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting request:', error);
      showToast('Failed to reject request. Please try again.');
    } finally {
      setRejectingRequest(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden font-sans">
      {/* Sidebar */}
      <aside className="w-72 bg-white border-r border-gray-100 flex flex-col shadow-2xl z-20">
        <div className="h-20 flex items-center px-8 border-b border-gray-100 bg-white">
          <div className="h-10 w-10 bg-white rounded-xl flex items-center justify-center p-1 shadow-lg shadow-emerald-500/10 border border-gray-100 mr-3">
            <img src="/dashboard_logo.png" alt="Polyglots" className="h-full w-full object-contain" />
          </div>
          <div className="flex flex-col">
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 leading-none">
              Polyglots
            </span>
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
              Admin Portal
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-6 px-4 space-y-1 admin-invisible-scrollbar">
          <div className="px-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Main</div>

          <SidebarItem
            icon={FileText}
            label="Overview"
            active={activeTab === 'overview'}
            onClick={() => navigate('/admin/overview')}
          />

          <div className="px-4 mt-6 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Management</div>

          <button
            onClick={() => setApprovalsMenuOpen(!approvalsMenuOpen)}
            className={`w-full flex items-center justify-between px-4 py-3 mb-2 rounded-xl transition-all duration-200 group ${activeTab === 'approvals'
              ? 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 text-emerald-600 shadow-sm border border-emerald-100'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg transition-colors ${activeTab === 'approvals' ? 'bg-emerald-500 text-white' : 'bg-gray-100/50 text-gray-400 group-hover:bg-white group-hover:text-emerald-500 group-hover:shadow-sm'}`}>
                <UserCheck size={20} />
              </div>
              <span className={`font-medium ${activeTab === 'approvals' ? 'font-semibold' : ''}`}>Approvals</span>
            </div>
            <ChevronRight size={16} className={`text-gray-400 transition-transform duration-200 ${approvalsMenuOpen ? 'rotate-90' : ''}`} />
          </button>

          {approvalsMenuOpen && (
            <div className="ml-4 pl-4 border-l border-gray-200 space-y-1 mb-2 animate-slideDown">
              <button onClick={() => { navigate('/admin/approvals'); setActiveApprovalTab('pending'); }} className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${activeTab === 'approvals' && activeApprovalTab === 'pending' ? 'text-emerald-600 font-medium bg-emerald-50' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                Pending
              </button>
              <button onClick={() => { navigate('/admin/approvals'); setActiveApprovalTab('approved'); }} className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${activeTab === 'approvals' && activeApprovalTab === 'approved' ? 'text-emerald-600 font-medium bg-emerald-50' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                Approved
              </button>
              <button onClick={() => { navigate('/admin/approvals'); setActiveApprovalTab('rejected'); }} className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${activeTab === 'approvals' && activeApprovalTab === 'rejected' ? 'text-emerald-600 font-medium bg-emerald-50' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                Rejected
              </button>
            </div>
          )}

          <SidebarItem
            icon={Eye}
            label="Recruiter Visibility"
            active={activeTab === 'recruiter-visibility'}
            onClick={() => navigate('/admin/recruiter-visibility')}
          />

          <SidebarItem
            icon={FileText}
            label="Recruiter Requests"
            active={activeTab === 'recruiter-requests'}
            onClick={() => navigate('/admin/recruiter-requests')}
          />

          <SidebarItem
            icon={Briefcase}
            label="Recruiter Interests"
            active={activeTab === 'recruiter-interests'}
            onClick={() => { navigate('/admin/recruiter-interests'); fetchRecruiterInterests(interestStatusFilter, interestTypeFilter); }}
          />

          <SidebarItem
            icon={MessageSquare}
            label="Recruiter Feedbacks"
            active={activeTab === 'recruiter-feedbacks'}
            onClick={() => {
              navigate('/admin/recruiter-feedbacks');
              setFeedbackPartnerTypeFilter('recruiting_partner');
              fetchPartnerFeedbacks();
            }}
            badge={partnerFeedbacks.filter(f => f.status === 'new' && f.partner_type === 'recruiting_partner').length || null}
          />

          <div className="px-4 mt-6 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Partners</div>

          <SidebarItem
            icon={Users}
            label="Sourcing Partners"
            active={activeTab === 'sourcing-partners'}
            onClick={() => { navigate('/admin/sourcing-partners'); setShowPartnerCandidates(false); setSelectedSourcingPartner(null); fetchSourcingPartners(); fetchSourcingCandidates(); }}
          />

          <SidebarItem
            icon={MessageSquare}
            label="Sourcing Feedbacks"
            active={activeTab === 'sourcing-feedbacks'}
            onClick={() => {
              navigate('/admin/sourcing-feedbacks');
              setFeedbackPartnerTypeFilter('sourcing_partner');
              fetchPartnerFeedbacks();
            }}
            badge={partnerFeedbacks.filter(f => f.status === 'new' && f.partner_type === 'sourcing_partner').length || null}
          />

          <div className="px-4 mt-6 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">System</div>

          <SidebarItem
            icon={Bell}
            label="Notifications"
            active={activeTab === 'notifications'}
            onClick={() => { navigate('/admin/notifications'); fetchNotifications(); }}
            badge={unreadCount > 0 ? unreadCount : null}
          />

          <SidebarItem
            icon={MessageSquare}
            label="Messages"
            active={activeTab === 'messages'}
            onClick={() => { navigate('/messages'); }}
            badge={null}
          />
        </div>

        <div className="p-4 border-t border-gray-100 bg-gray-50/50">
          <button onClick={handleLogout} className="w-full flex items-center px-4 py-3 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors">
            <LogOut className="h-5 w-5 mr-3" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Header */}
        <header className="h-20 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center justify-between px-8 z-40 sticky top-0">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-800 tracking-tight">
              {activeTab === 'overview' && 'Overview'}
              {activeTab === 'approvals' && 'User Approvals'}
              {activeTab === 'recruiter-visibility' && 'Recruiter Visibility'}
              {activeTab === 'recruiter-requests' && 'Recruiter Requests'}
              {activeTab === 'recruiter-interests' && 'Recruiter Interests'}
              {activeTab === 'sourcing-partners' && 'Sourcing Partners'}
              {activeTab === 'partner-feedbacks' && 'Partner Feedbacks'}
              {activeTab === 'recruiter-feedbacks' && 'Recruiter Feedbacks'}
              {activeTab === 'sourcing-feedbacks' && 'Sourcing Feedbacks'}
              {activeTab === 'notifications' && 'Notifications'}
            </h2>
            {activeTab === 'approvals' && (
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold uppercase tracking-wide">
                {activeApprovalTab}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-5">
            {/* Search */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Quick search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:bg-white transition-all w-64 text-gray-900"
              />
            </div>

            <div className="h-8 w-px bg-gray-200 mx-2"></div>

            <NotificationBell userRole="admin" onNotificationClick={handleNotificationClick} />

            <div className="relative group">
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center space-x-3 focus:outline-none"
              >
                <div className="h-10 w-10 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-lg shadow-md shadow-emerald-500/20 ring-2 ring-white">
                  {currentUser?.name?.charAt(0)?.toUpperCase() || 'A'}
                </div>
                <div className="text-left hidden md:block">
                  <p className="text-sm font-bold text-gray-800">{currentUser?.name || 'Admin User'}</p>
                  <p className="text-xs text-gray-500">Administrator</p>
                </div>
                <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </button>
              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 animate-slideUp origin-top-right">
                  <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/50">
                    <p className="text-sm font-semibold text-gray-900">{currentUser?.name || 'Admin User'}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{currentUser?.email || ''}</p>
                  </div>
                  <button
                    onClick={() => {
                      setPasswordResetUserId(null);
                      setShowPasswordModal(true);
                      setShowProfileDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center space-x-2 transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Reset My Password</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6 relative z-0 admin-invisible-scrollbar">


          {activeTab === 'profile' && (
            <div className="animate-fadeIn">
              <CandidateProfileView candidate={null} isPageView={true} embedded={true} />
            </div>
          )}

          {activeTab === 'interviews' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center bg-white rounded-xl shadow-sm border border-emerald-100 p-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Interviews</h2>
                  <p className="text-sm text-gray-500 mt-1">Manage your interview schedule and joining links.</p>
                </div>
                <button
                  onClick={() => setShowScheduleInterviewModal(true)}
                  className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Schedule New
                </button>
              </div>

              <div className="space-y-4">
                {loading ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                  </div>
                ) : interviews.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-gray-100 shadow-sm">
                    <Calendar className="mx-auto h-12 w-12 text-emerald-100 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">No interviews scheduled</h3>
                    <p className="text-gray-500">Get started by scheduling a new interview.</p>
                  </div>
                ) : (
                  interviews.map(interview => {
                    const statusColors = {
                      'scheduled': 'bg-amber-100 text-amber-700 border-amber-200',
                      'completed': 'bg-emerald-100 text-emerald-700 border-emerald-200',
                      'cancelled': 'bg-red-100 text-red-700 border-red-200'
                    };

                    return (
                      <div key={interview.id} className="flex flex-col sm:flex-row items-start sm:items-center bg-white rounded-2xl p-6 shadow-sm border border-gray-100 gap-6 transition-all hover:shadow-md">
                        <div className="flex flex-col items-center justify-center min-w-[100px] bg-indigo-50/50 rounded-xl p-3 border border-indigo-50/50">
                          <span className="text-2xl font-bold text-indigo-600 tracking-tight">
                            {interview.time?.substring(0, 5) || 'TBA'}
                          </span>
                          <span className="text-sm font-medium text-indigo-400 mt-1 uppercase">
                            {new Date(interview.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </span>
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-lg font-bold text-gray-900">{interview.candidate_name}</h3>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusColors[interview.status] || 'bg-gray-100 text-gray-700'}`}>
                              {interview.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                            <span>{interview.job_title || 'General Interview'}</span>
                          </div>
                        </div>

                        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                          <div className="text-right hidden sm:block">
                            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Interviewer</div>
                            <div className="text-sm font-semibold text-gray-800">{interview.interviewer_name}</div>
                          </div>

                          <div className="flex items-center gap-2">
                            {interview.meeting_link ? (
                              <a
                                href={interview.meeting_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-bold rounded-xl transition-colors shadow-sm shadow-blue-500/20 flex items-center gap-2"
                              >
                                <Video className="w-4 h-4" />
                                Join Meeting
                              </a>
                            ) : (
                              <span className="px-4 py-2 bg-gray-100 text-gray-500 text-sm font-medium rounded-xl">
                                No Link
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          )}

          {/* Stats Cards - Modern Design - Show on Overview tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6 animate-fadeIn">
              {/* Primary Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Pending Users */}
                <div
                  onClick={() => { navigate('/admin/approvals'); setActiveApprovalTab('pending'); }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative group overflow-hidden cursor-pointer"
                >
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <UserPlus className="h-16 w-16 text-amber-500" />
                  </div>
                  <div className="relative z-10">
                    <div className="h-12 w-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <UserPlus className="h-6 w-6 text-amber-600" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800 mb-1">{stats.pending}</p>
                    <p className="text-sm font-medium text-slate-500">Pending Approvals</p>
                  </div>
                </div>

                {/* Approved Users */}
                <div
                  onClick={() => { navigate('/admin/approvals'); setActiveApprovalTab('approved'); }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative group overflow-hidden cursor-pointer"
                >
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <CheckCircle className="h-16 w-16 text-emerald-500" />
                  </div>
                  <div className="relative z-10">
                    <div className="h-12 w-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <CheckCircle className="h-6 w-6 text-emerald-600" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800 mb-1">{stats.approved}</p>
                    <p className="text-sm font-medium text-slate-500">Active Users</p>
                  </div>
                </div>

                {/* Total Candidates */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative group overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Users className="h-16 w-16 text-blue-500" />
                  </div>
                  <div className="relative z-10">
                    <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800 mb-1">{stats.candidates}</p>
                    <p className="text-sm font-medium text-slate-500">Total Candidates</p>
                  </div>
                </div>

                {/* Recruiters */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative group overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Briefcase className="h-16 w-16 text-purple-500" />
                  </div>
                  <div className="relative z-10">
                    <div className="h-12 w-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Briefcase className="h-6 w-6 text-purple-600" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800 mb-1">{stats.recruiters}</p>
                    <p className="text-sm font-medium text-slate-500">Employers</p>
                  </div>
                </div>
              </div>

              {/* Candidate Statistics Section */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">Candidate Pool Analytics</h2>
                    <p className="text-sm text-slate-500">Breakdown by occupation and expertise</p>
                  </div>
                  <div className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-lg text-sm font-medium border border-emerald-100">
                    Total Visible: {totalCandidates}
                  </div>
                </div>
                <div className="p-6 bg-slate-50/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {candidateStatistics.filter(stat => stat.occupation?.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 ? (
                      <div className="col-span-full flex flex-col items-center justify-center py-12 text-slate-400 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                        <Users className="h-12 w-12 mb-3 opacity-50" />
                        <p>No analytics data available yet</p>
                      </div>
                    ) : (
                      candidateStatistics
                        .filter(stat => stat.occupation?.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map((stat, index) => (
                          <div
                            key={index}
                            onClick={() => handleOccupationClick(stat.occupation)}
                            className="bg-white hover:bg-blue-50/30 p-4 rounded-xl border border-slate-200 hover:border-blue-200 shadow-sm transition-all group cursor-pointer"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="p-2 bg-slate-100 rounded-lg group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors text-slate-500">
                                <Briefcase className="h-4 w-4" />
                              </div>
                              <span className="text-2xl font-bold text-slate-800 group-hover:text-blue-700 transition-colors">{stat.count}</span>
                            </div>
                            <h3 className="text-sm font-bold text-slate-700 truncate" title={stat.occupation}>{stat.occupation}</h3>
                            <div className="w-full bg-slate-100 h-1.5 rounded-full mt-3 overflow-hidden">
                              <div className="bg-blue-500 h-full rounded-full" style={{ width: `${(stat.count / totalCandidates) * 100}%` }}></div>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                </div>
              </div>

              {/* Manual Pools Section */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">Manual Talent Pools</h2>
                    <p className="text-sm text-slate-500">Custom candidate groups for faster segregation</p>
                  </div>
                  <button
                    onClick={() => setShowCreatePoolModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-500/20 hover:bg-blue-700 transition-all"
                  >
                    <Plus className="h-4 w-4" />
                    Create New Pool
                  </button>
                </div>
                <div className="p-6 bg-slate-50/50">
                  {loadingPools ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : manualPools.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-400 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                      <Database className="h-12 w-12 mb-3 opacity-50" />
                      <p>No manual pools created yet</p>
                      <button
                        onClick={() => setShowCreatePoolModal(true)}
                        className="mt-3 text-blue-600 font-bold hover:underline"
                      >
                        Create your first pool
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {manualPools.map((pool) => (
                        <div
                          key={pool.id}
                          className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm transition-all group relative text-left"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
                              <Database className="h-5 w-5" />
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeletePool(pool.id); }}
                              className="p-2 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                              title="Delete Pool"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <h3 className="text-lg font-bold text-slate-800 mb-1 truncate" title={pool.name}>{pool.name}</h3>
                          <div className="flex items-center gap-2 text-slate-500 text-sm mb-4">
                            <Users className="h-4 w-4" />
                            <span>{pool.candidate_count} candidates</span>
                          </div>
                          <button
                            onClick={() => fetchPoolCandidates(pool)}
                            className="w-full py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-bold hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center gap-2"
                          >
                            <Eye className="h-4 w-4" />
                            View Candidates
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Approvals Tab Content */}
          {activeTab === 'approvals' && (
            <>
              {loading && (
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading users...</p>
                  </div>
                </div>
              )}
              {/* Filters and Search */}
              <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-5 mb-6">
                <div className="flex flex-wrap gap-4 items-center justify-between">
                  <div className="flex flex-wrap gap-4 items-center flex-1">
                    <div className="flex-1 min-w-[200px]">
                      <div className="relative group">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors h-5 w-5" />
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all bg-gray-50 focus:bg-white text-gray-900"
                        />
                      </div>
                    </div>
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 focus:bg-white cursor-pointer text-gray-900"
                    >
                      <option value="all">All Roles</option>
                      <option value="candidate">Candidate</option>
                      <option value="staff">Staff</option>
                      <option value="recruiting_partner">Employer</option>
                      <option value="sourcing_partner">Sourcing Partner</option>
                    </select>
                  </div>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-medium shadow-md shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-700 transition-all transform hover:-translate-y-0.5 flex items-center space-x-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Create User</span>
                  </button>
                </div>
              </div>

              {/* Users Table */}
              <div className="bg-white rounded-xl shadow-sm border border-emerald-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-50/50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Profile Completion
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-50">
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                            <div className="flex flex-col items-center justify-center">
                              <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                <Users className="h-6 w-6 text-gray-400" />
                              </div>
                              <p className="font-medium">No users found</p>
                              <p className="text-sm mt-1">Try adjusting your filters</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map((u) => (
                          <tr key={u.id} className="hover:bg-emerald-50/30 transition-colors group cursor-pointer" onClick={() => handleViewUser(u)}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 relative">
                                  {u.photo_url ? (
                                    <>
                                      <img
                                        src={getPhotoUrl(u.photo_url, u.updated_at)}
                                        alt={u.name}
                                        className="h-10 w-10 rounded-full object-cover border-2 border-white shadow-sm ring-1 ring-gray-100"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          const fallback = e.target.nextElementSibling;
                                          if (fallback) fallback.style.display = 'flex';
                                        }}
                                        onLoad={(e) => {
                                          const fallback = e.target.nextElementSibling;
                                          if (fallback) fallback.style.display = 'none';
                                        }}
                                      />
                                      <div className="h-10 w-10 bg-emerald-100 rounded-full items-center justify-center hidden" style={{ display: 'none' }}>
                                        {getRoleIcon(u.role)}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="h-10 w-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600">
                                      {getRoleIcon(u.role)}
                                    </div>
                                  )}
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors">{u.name}</div>
                                  <div className="flex items-center gap-1">
                                    <div className="text-xs text-gray-500">
                                      {u.role === 'recruiting_partner' ? 'Hidden for privacy' : u.email}
                                    </div>
                                    {u.email_verified && u.role !== 'recruiting_partner' && (
                                      <BadgeCheck className="h-3 w-3 text-blue-500" aria-label="Verified Email" />
                                    )}
                                  </div>
                                  {u.role === 'sourcing_partner' && (u.agency_name || u.agency_phone || u.agency_address) && (
                                    <div className="text-xs text-gray-400 mt-1 flex items-center space-x-2">
                                      {u.agency_name && <span className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{u.agency_name}</span>}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className={`inline-flex items-center space-x-2 px-2.5 py-1 rounded-lg text-xs font-medium border ${u.role === 'admin' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' :
                                  u.role === 'staff' ? 'bg-purple-50 text-purple-700 border-purple-100' :
                                    u.role === 'recruiting_partner' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                      u.role === 'sourcing_partner' ? 'bg-orange-50 text-orange-700 border-orange-100' :
                                        'bg-green-50 text-green-700 border-green-100'
                                  }`}>
                                  {getRoleIcon(u.role)}
                                  <span className="capitalize">{u.role?.replace('_', ' ')}</span>
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getStatusBadge(u.approval_status)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {u.role === 'candidate' ? (
                                (() => {
                                  const { score, missingFields } = calculateProfileCompletion(u);
                                  return (
                                    <div className="flex flex-col gap-1">
                                      <div className="flex items-center gap-2">
                                        <div className="w-16 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                          <div
                                            className={`h-full rounded-full transition-all ${score === 100 ? 'bg-emerald-500' :
                                              score > 50 ? 'bg-blue-500' : 'bg-amber-500'
                                              }`}
                                            style={{ width: `${score}%` }}
                                          />
                                        </div>
                                        <span className={`text-xs font-bold ${score === 100 ? 'text-emerald-600' :
                                          score > 50 ? 'text-blue-600' : 'text-amber-600'
                                          }`}>
                                          {score}%
                                        </span>
                                      </div>
                                      {missingFields.length > 0 && (
                                        <div className="mt-1">
                                          <div className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1">Missing Details:</div>
                                          <div className="flex flex-wrap gap-1">
                                            {missingFields.map((f, i) => (
                                              <span key={i} className="px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded text-[9px] border border-amber-100 font-medium">
                                                {f}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleViewUser(u); }}
                                  className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                  title="View Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>

                                {u.role !== 'admin' && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleImpersonate(u); }}
                                    className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                    title="Impersonate User"
                                  >
                                    <LogIn className="h-4 w-4" />
                                  </button>
                                )}

                                {u.approval_status === 'pending' && (
                                  <>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleApprove(u.id); }}
                                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      title="Approve"
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleReject(u.id); }}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Reject"
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </button>
                                  </>
                                )}

                                {u.approval_status === 'rejected' && (
                                  <>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleApprove(u.id); }}
                                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      title="Approve"
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleUpdateStatus(u.id, 'pending'); }}
                                      className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                      title="Move to Pending"
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </button>
                                  </>
                                )}

                                {u.role === 'candidate' && u.approval_status === 'approved' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMessageRecipient(u);
                                      setShowMessageModal(true);
                                    }}
                                    className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                    title="Send Message"
                                  >
                                    <MessageSquare className="h-4 w-4" />
                                  </button>
                                )}

                                {u.role === 'candidate' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCandidateToAddToPool(u);
                                      setShowAddToPoolModal(true);
                                    }}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Add to Pool"
                                  >
                                    <Database className="h-4 w-4" />
                                  </button>
                                )}

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPasswordResetUserId(u.id);
                                    setSelectedUser(u);
                                    setShowPasswordModal(true);
                                  }}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Reset Password"
                                >
                                  <Key className="h-4 w-4" />
                                </button>

                                {u.role !== 'admin' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedUser(u);
                                      setEditUserData({
                                        name: u.name || '',
                                        qualification: u.qualification || '',
                                        experience: u.experience || '',
                                        german_level: u.german_level || '',
                                        phone: u.phone || '',
                                        university: u.university || '',
                                        year_of_passout: u.year_of_passout || '',
                                        occupation: u.occupation || '',
                                        profile_status: u.profile_status || 'incomplete'
                                      });
                                      setShowEditModal(true);
                                    }}
                                    className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title="Edit User"
                                  >
                                    <Settings className="h-4 w-4" />
                                  </button>
                                )}

                                {u.email !== 'admin@polygotstalent.com' && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete(u.id); }}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete User"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6 flex flex-wrap justify-between items-center gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Notifications</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Track requests and system updates.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Date</label>
                    <select
                      value={notificationDateSort}
                      onChange={(e) => setNotificationDateSort(e.target.value)}
                      className="px-4 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors cursor-pointer"
                    >
                      <option value="desc">Newest First</option>
                      <option value="asc">Oldest First</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Status</label>
                    <select
                      value={notificationStatusFilter}
                      onChange={(e) => setNotificationStatusFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors cursor-pointer"
                    >
                      <option value="all">All</option>
                      <option value="unread">Unread</option>
                      <option value="read">Read</option>
                    </select>
                  </div>
                </div>
              </div>

              {notifications.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
                  <div className="h-20 w-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Bell className="h-10 w-10 text-emerald-300" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">No notifications</h3>
                  <p className="text-gray-500 mt-1">You're all caught up!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {([...notifications]
                    // filter by read / unread status
                    .filter(notification => {
                      if (notificationStatusFilter === 'unread') {
                        return !notification.is_read;
                      }
                      if (notificationStatusFilter === 'read') {
                        return notification.is_read;
                      }
                      return true;
                    })
                    // sort by created_at
                    .sort((a, b) => {
                      const da = new Date(a.created_at).getTime();
                      const db = new Date(b.created_at).getTime();
                      return notificationDateSort === 'asc' ? da - db : db - da;
                    }))
                    .map(notification => (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`bg-white rounded-xl p-5 border shadow-sm transition-all hover:shadow-md cursor-pointer relative overflow-hidden group ${notification.is_read
                          ? 'border-gray-100'
                          : 'border-emerald-100 bg-emerald-50/10'
                          }`}
                      >
                        {!notification.is_read && (
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500"></div>
                        )}
                        <div className="flex items-start space-x-4">
                          <div className={`flex-shrink-0 p-3 rounded-xl ${!notification.is_read ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'
                            }`}>
                            {notification.type === 'recruiter_interest' || notification.type === 'pool_interest' ? (
                              <UserCheck className="h-6 w-6" />
                            ) : notification.type === 'resume_download' ? (
                              <Download className="h-6 w-6" />
                            ) : (
                              <Bell className="h-6 w-6" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              {!notification.is_read && (
                                <span className="bg-emerald-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5 uppercase tracking-wide">
                                  New
                                </span>
                              )}
                              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${notification.type === 'recruiter_interest' || notification.type === 'pool_interest'
                                ? 'bg-blue-50 text-blue-600'
                                : notification.type === 'resume_download'
                                  ? 'bg-purple-50 text-purple-600'
                                  : 'bg-gray-100 text-gray-600'
                                }`}>
                                {notification.type?.replace('_', ' ')}
                              </span>
                              <span className="text-xs text-gray-400 ml-auto">
                                {new Date(notification.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className={`text-gray-900 ${!notification.is_read ? 'font-semibold' : 'font-medium'}`}>
                              {notification.message}
                            </p>

                            {notification.type === 'recruiter_interest' && notification.reference_id && (
                              <div className="mt-4 flex space-x-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApproveInterest(notification);
                                  }}
                                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium flex items-center space-x-2 transition-colors shadow-sm shadow-emerald-600/20"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                  <span>Approve Interest</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsRead(notification.id);
                                  }}
                                  className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                                >
                                  Mark as Read
                                </button>
                              </div>
                            )}
                          </div>
                          {!notification.is_read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notification.id);
                              }}
                              className="flex-shrink-0 text-gray-400 hover:text-emerald-500 p-2 rounded-lg hover:bg-emerald-50 transition-colors opacity-0 group-hover:opacity-100"
                              title="Mark as read"
                            >
                              <CheckCircle className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}



          {/* Candidate Pool Statistics by Occupation - Only show on recruiter-visibility tab */}
          {activeTab === 'recruiter-visibility' && (
            <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Candidate Pool</h2>
                <div className="bg-emerald-50 px-4 py-2 rounded-lg border border-emerald-100">
                  <p className="text-sm font-medium text-emerald-800">
                    Total Approved: <span className="text-2xl font-bold ml-2">{totalCandidates}</span>
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {candidateStatistics.length === 0 ? (
                  <p className="text-gray-500 col-span-full text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">No candidate statistics available</p>
                ) : (
                  candidateStatistics.map((stat, index) => (
                    <div key={index} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-all flex items-center justify-between group">
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-1 group-hover:text-emerald-600 transition-colors">{stat.occupation || 'Unspecified'}</p>
                        <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
                      </div>
                      <div className="h-10 w-10 bg-gray-50 rounded-lg flex items-center justify-center group-hover:bg-emerald-50 transition-colors">
                        <Users className="h-5 w-5 text-gray-400 group-hover:text-emerald-500" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Recruiter Visibility Tab */}
          {activeTab === 'recruiter-visibility' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Manage Visibility</h2>
                <div className="mb-6 max-w-xl">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Recruiter</label>
                  <div className="relative">
                    <select
                      value={selectedRecruiter?.id || ''}
                      onChange={(e) => {
                        const recruiter = recruiters.find(r => r.id === parseInt(e.target.value));
                        setSelectedRecruiter(recruiter);
                        if (recruiter) {
                          fetchVisibleCandidates(recruiter.id);
                        }
                      }}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 focus:bg-white appearance-none cursor-pointer"
                    >
                      <option value="">Select a recruiter...</option>
                      {recruiters.map(recruiter => (
                        <option key={recruiter.id} value={recruiter.id}>{recruiter.name} ({recruiter.email})</option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500">
                      <ChevronDown className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                {selectedRecruiter && (
                  <div className="mt-8 border-t border-gray-100 pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                      <Eye className="h-5 w-5 mr-2 text-emerald-500" />
                      Manage Visibility for <span className="text-emerald-600 ml-1">{selectedRecruiter.name}</span>
                      <button
                        onClick={() => handleImpersonate(selectedRecruiter)}
                        className="ml-4 p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-all flex items-center gap-2 text-xs font-bold border border-emerald-200"
                        title="Impersonate this recruiter"
                      >
                        <UserCheck className="h-4 w-4" /> Impersonate
                      </button>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* Available Candidates */}
                      <div className="bg-white rounded-xl shadow-sm border border-emerald-100 flex flex-col h-[500px]">
                        <div className="p-4 border-b border-gray-100 bg-gray-50/50 rounded-t-xl">
                          <h4 className="font-semibold text-gray-900 flex items-center">
                            <span className="bg-gray-200 text-gray-600 text-xs font-bold px-2 py-0.5 rounded-full mr-2">
                              {allCandidatesForVisibility.filter(c => !visibleCandidates.some(vc => vc.id === c.id)).length}
                            </span>
                            Available Candidates
                          </h4>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 admin-invisible-scrollbar">
                          {allCandidatesForVisibility
                            .filter(c => !visibleCandidates.some(vc => vc.id === c.id))
                            .map(candidate => (
                              <div key={candidate.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all group">
                                <div className="flex items-center space-x-3">
                                  <div className="relative">
                                    {candidate.photo_url ? (
                                      <img
                                        src={getPhotoUrl(candidate.photo_url, candidate.updated_at)}
                                        alt={candidate.name}
                                        className="h-10 w-10 rounded-full object-cover border border-gray-100"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          const fallback = e.target.nextElementSibling;
                                          if (fallback) fallback.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 group-hover:bg-emerald-50 group-hover:text-emerald-500 transition-colors" style={{ display: candidate.photo_url ? 'none' : 'flex' }}>
                                      <GraduationCap className="h-5 w-5" />
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors">{candidate.name}</p>
                                    <p className="text-xs text-gray-500">{candidate.email}</p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleGrantVisibility(selectedRecruiter.id, candidate.id)}
                                  className="p-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-500 hover:text-white transition-all shadow-sm"
                                  title="Grant Access"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          {allCandidatesForVisibility.filter(c => !visibleCandidates.some(vc => vc.id === c.id)).length === 0 && (
                            <div className="text-center py-12 text-gray-400">
                              <Users className="h-12 w-12 mx-auto mb-2 opacity-20" />
                              <p>No more candidates available</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Granted Candidates */}
                      <div className="bg-white rounded-xl shadow-sm border border-emerald-100 flex flex-col h-[500px]">
                        <div className="p-4 border-b border-gray-100 bg-gray-50/50 rounded-t-xl">
                          <h4 className="font-semibold text-gray-900 flex items-center">
                            <span className="bg-emerald-100 text-emerald-600 text-xs font-bold px-2 py-0.5 rounded-full mr-2">
                              {visibleCandidates.length}
                            </span>
                            Granted Access
                          </h4>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 admin-invisible-scrollbar">
                          {visibleCandidates.length === 0 ? (
                            <div className="text-center py-12 text-gray-400">
                              <EyeOff className="h-12 w-12 mx-auto mb-2 opacity-20" />
                              <p>No candidates granted yet</p>
                            </div>
                          ) : (
                            visibleCandidates.map(candidate => (
                              <div key={candidate.id} className="flex items-center justify-between p-3 bg-emerald-50/30 rounded-xl border border-emerald-100 shadow-sm transition-all group">
                                <div className="flex items-center space-x-3">
                                  <div className="relative">
                                    {candidate.photo_url ? (
                                      <img
                                        src={getPhotoUrl(candidate.photo_url, candidate.updated_at)}
                                        alt={candidate.name}
                                        className="h-10 w-10 rounded-full object-cover border border-emerald-100 ring-2 ring-emerald-50"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          const fallback = e.target.nextElementSibling;
                                          if (fallback) fallback.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600" style={{ display: candidate.photo_url ? 'none' : 'flex' }}>
                                      <CheckCircle className="h-5 w-5" />
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900">{candidate.name}</p>
                                    <p className="text-xs text-gray-500">{candidate.email}</p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRevokeVisibility(selectedRecruiter.id, candidate.id)}
                                  className="p-2 bg-white text-red-400 border border-gray-100 rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition-all"
                                  title="Revoke Access"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recruiter Requests Tab */}
          {activeTab === 'recruiter-requests' && !showRequestCandidates && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Recruiter Requests</h2>
                <p className="text-sm text-gray-500">Manage talent requests from recruiters</p>
              </div>

              {recruiterRequests.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
                  <div className="h-20 w-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="h-10 w-10 text-emerald-300" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">No requests found</h3>
                  <p className="text-gray-500 mt-1">Check back later for new requests.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recruiterRequests.map(request => (
                    <div key={request.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all group relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-full blur-3xl opacity-50 -mr-16 -mt-16 transition-opacity group-hover:opacity-100"></div>

                      <div className="flex flex-col md:flex-row justify-between items-start gap-4 relative z-10 w-full">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${request.status === 'approved' ? 'bg-green-100 text-green-800' :
                              request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                              {request.status || 'pending'}
                            </span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-500">{new Date(request.created_at).toLocaleDateString()}</span>
                          </div>
                          <h3 className="font-bold text-gray-900 text-lg mb-1 group-hover:text-emerald-700 transition-colors">
                            {request.requirements || `Request #${request.id}`}
                          </h3>
                          <div className="flex items-center text-sm text-gray-600 mb-3">
                            <span className="font-medium mr-1">From:</span>
                            {request.recruiter_name || 'Unknown'}
                            <span className="text-gray-400 mx-1">&lt;Hidden for privacy&gt;</span>
                          </div>

                          {request.requirements && (
                            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 border border-gray-100">
                              <p>{request.requirements}</p>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 items-center flex-wrap md:flex-nowrap">
                          {request.status === 'pending' && (
                            <button
                              onClick={() => {
                                setRequestToReject(request);
                                setShowRejectRequestModal(true);
                              }}
                              className="px-5 py-2.5 bg-white border border-red-200 text-red-600 rounded-xl hover:bg-red-50 text-sm font-medium transition-all transform hover:-translate-y-0.5 whitespace-nowrap"
                            >
                              Decline Request
                            </button>
                          )}
                          {request.admin_comment && request.status !== 'pending' && (
                            <div className="absolute top-4 right-4 bg-gray-50 text-gray-600 text-xs px-2 py-1 rounded border border-gray-200 z-10 hidden group-hover:block" title="Admin Comment">
                              {request.admin_comment}
                            </div>
                          )}
                          <button
                            onClick={() => handleManageCandidates(request)}
                            className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 text-sm font-medium shadow-sm shadow-emerald-200 transition-all transform hover:-translate-y-0.5 whitespace-nowrap"
                          >
                            Manage Candidates
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Request Candidates View - Only show when in recruiter-requests tab */}
          {activeTab === 'recruiter-requests' && showRequestCandidates && selectedRequest && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setShowRequestCandidates(false);
                    setSelectedRequest(null);
                    setSelectedCandidatesCart([]);
                    setApprovedCandidatesForRequest([]);
                  }}
                  className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 flex items-center space-x-2 transition-all shadow-sm"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Requests</span>
                </button>
              </div>

              {/* Request Info */}
              <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-50 rounded-full blur-3xl opacity-30 -mr-32 -mt-32"></div>
                <h2 className="text-xl font-bold text-gray-900 mb-2 relative z-10">
                  {selectedRequest.title || selectedRequest.requirements || `Request #${selectedRequest.id}`}
                </h2>
                <div className="flex items-center text-sm text-gray-600 mb-4 relative z-10">
                  <User className="h-4 w-4 mr-1 text-gray-400" />
                  <span className="font-medium mr-1">Requester:</span>
                  {selectedRequest.recruiter_name || 'Unknown'} (Hidden for privacy)
                </div>
                {selectedRequest.requirements && (
                  <div className="bg-gray-50/80 rounded-lg p-4 text-sm text-gray-600 border border-gray-100 relative z-10">
                    <p>{selectedRequest.requirements}</p>
                  </div>
                )}
              </div>

              {/* Cart Summary - Sticky */}
              {selectedCandidatesCart.length > 0 && (
                <div className="sticky top-4 z-30">
                  <div className="bg-emerald-600 rounded-xl p-4 shadow-lg shadow-emerald-500/30 text-white backdrop-blur-md bg-opacity-95 border border-emerald-500">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="bg-white/20 p-2 rounded-lg">
                          <ShoppingCart className="h-5 w-5 text-white" />
                        </div>
                        <span className="font-bold text-lg">
                          {selectedCandidatesCart.length} <span className="text-emerald-100 text-base font-normal">candidate{selectedCandidatesCart.length !== 1 ? 's' : ''} selected</span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setSelectedCandidatesCart([])}
                          className="px-3 py-1.5 text-sm text-emerald-100 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                          Clear Selection
                        </button>
                        <button
                          onClick={() => handleApproveCandidateForRequest(selectedCandidatesCart)}
                          className="px-5 py-2 bg-white text-emerald-600 rounded-lg hover:bg-emerald-50 font-bold shadow-sm transition-all transform hover:scale-105"
                        >
                          Approve Selected
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Search and Filters */}
              <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Select Candidates</h3>

                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative group">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                    <input
                      type="text"
                      placeholder="Search candidates by name, email or qualification..."
                      value={candidateSearchTerm}
                      onChange={(e) => setCandidateSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 focus:bg-white transition-all shadow-sm text-gray-900"
                    />
                  </div>
                </div>

                {/* Occupation Category Tags */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedOccupationFilter('all')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedOccupationFilter === 'all'
                        ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
                        : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                        }`}
                    >
                      All Roles <span className="ml-1 opacity-80">({allCandidatesForVisibility.length})</span>
                    </button>
                    {getOccupationCategories().map(occupation => {
                      const count = allCandidatesForVisibility.filter(c =>
                        occupation === 'Not Specified' ? !c.occupation : c.occupation === occupation
                      ).length;
                      if (count === 0) return null;

                      return (
                        <button
                          key={occupation}
                          onClick={() => setSelectedOccupationFilter(occupation)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedOccupationFilter === occupation
                            ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                            }`}
                        >
                          {occupation} <span className="ml-1 opacity-80">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Candidates Grid */}
                <div className="space-y-3 max-h-[600px] overflow-y-auto admin-invisible-scrollbar p-1">
                  {(() => {
                    const filteredCandidates = getFilteredCandidates();

                    if (allCandidatesForVisibility.length === 0) {
                      return (
                        <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                          <Users className="h-12 w-12 mx-auto mb-2 opacity-20" />
                          <p>Loading candidates...</p>
                        </div>
                      );
                    }

                    if (filteredCandidates.length === 0) {
                      return (
                        <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                          <Search className="h-12 w-12 mx-auto mb-2 opacity-20" />
                          <p>No candidates found matching filters</p>
                          <button
                            onClick={() => {
                              setCandidateSearchTerm('');
                              setSelectedOccupationFilter('all');
                            }}
                            className="mt-2 text-emerald-600 hover:text-emerald-700 text-sm font-medium hover:underline"
                          >
                            Clear all filters
                          </button>
                        </div>
                      );
                    }

                    return filteredCandidates.map(candidate => {
                      const isInCart = selectedCandidatesCart.includes(candidate.id);
                      const isAlreadyApproved = approvedCandidatesForRequest.some(ac => ac.id === candidate.id);

                      return (
                        <div
                          key={candidate.id}
                          onClick={() => !isAlreadyApproved && toggleCandidateInCart(candidate.id)}
                          className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer group ${isInCart
                            ? 'bg-emerald-50 border-emerald-500 shadow-md ring-1 ring-emerald-500'
                            : isAlreadyApproved
                              ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                              : 'bg-white border-gray-100 hover:border-emerald-300 hover:shadow-md'
                            }`}
                        >
                          <div className="flex items-center space-x-4 flex-1">
                            {/* Checkbox */}
                            <div
                              className={`flex-shrink-0 h-6 w-6 rounded-lg border-2 flex items-center justify-center transition-all ${isInCart
                                ? 'bg-emerald-500 border-emerald-500 text-white scale-110'
                                : isAlreadyApproved
                                  ? 'bg-gray-200 border-gray-200 text-gray-400'
                                  : 'border-gray-300 group-hover:border-emerald-400 bg-white'
                                }`}
                            >
                              {(isInCart || isAlreadyApproved) && <Check className="h-4 w-4" />}
                            </div>

                            {/* Photo */}
                            <div className="relative">
                              {candidate.photo_url ? (
                                <img
                                  src={getPhotoUrl(candidate.photo_url, candidate.updated_at)}
                                  alt={candidate.name}
                                  className={`h-12 w-12 rounded-full object-cover border-2 ${isInCart ? 'border-emerald-500' : 'border-white shadow-sm'}`}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    const fallback = e.target.nextElementSibling;
                                    if (fallback) fallback.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`h-12 w-12 rounded-full flex items-center justify-center ${isInCart ? 'bg-emerald-200 text-emerald-700' : 'bg-gray-100 text-gray-400'}`} style={{ display: candidate.photo_url ? 'none' : 'flex' }}>
                                <Users className="h-6 w-6" />
                              </div>
                            </div>

                            {/* Candidate Info */}
                            <div className="flex-1 min-w-0">
                              <p className={`font-bold truncate ${isInCart ? 'text-emerald-900' : 'text-gray-900'}`}>{candidate.name}</p>
                              <p className="text-sm text-gray-500 truncate">{candidate.email}</p>
                              <div className="flex flex-wrap gap-2 mt-1.5">
                                {candidate.occupation && (
                                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full border border-gray-200">
                                    {candidate.occupation}
                                  </span>
                                )}
                                {candidate.qualification && (
                                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full border border-blue-100">
                                    {candidate.qualification}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {isAlreadyApproved && (
                            <span className="text-xs font-semibold px-3 py-1 bg-gray-200 text-gray-600 rounded-full">
                              Approved
                            </span>
                          )}
                          {!isAlreadyApproved && !isInCart && (
                            <button className="text-sm font-medium text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity px-4 py-2 bg-emerald-50 rounded-lg">
                              Select
                            </button>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Approved Candidates List */}
              {approvedCandidatesForRequest.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6 opacity-75 grayscale hover:grayscale-0 hover:opacity-100 transition-all">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-emerald-500" />
                    Already Approved for this Request ({approvedCandidatesForRequest.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto admin-invisible-scrollbar">
                    {approvedCandidatesForRequest.map(candidate => (
                      <div
                        key={candidate.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          {candidate.photo_url ? (
                            <img
                              src={getPhotoUrl(candidate.photo_url, candidate.updated_at)}
                              alt={candidate.name}
                              className="h-8 w-8 rounded-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const fallback = e.target.nextElementSibling;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <Users className="h-4 w-4 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate text-sm">{candidate.name}</p>
                            <p className="text-xs text-gray-500 truncate">{candidate.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveCandidateFromRequest(candidate.id)}
                          className="px-3 py-1 bg-white border border-red-200 text-red-600 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sourcing Partners Tab */}
          {activeTab === 'sourcing-partners' && (
            <div className="space-y-6">
              {/* Sourcing Partners List */}
              {!showPartnerCandidates && (
                <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Sourcing Partners</h2>
                      <p className="text-sm text-gray-500">Manage partners and their candidate pipelines</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sourcingPartners.map((partner) => (
                      <div
                        key={partner.id}
                        onClick={() => handleSelectSourcingPartner(partner)}
                        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg hover:border-emerald-200 cursor-pointer transition-all group relative overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 rounded-full blur-2xl opacity-0 group-hover:opacity-60 transition-opacity -mr-8 -mt-8"></div>
                        <div className="flex items-center justify-between mb-6 relative z-10">
                          <div className="flex items-center space-x-4">
                            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold text-xl shadow-md shadow-emerald-200">
                              {partner.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">{partner.name}</h3>
                              <p className="text-sm text-gray-500">{partner.email}</p>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImpersonate(partner);
                            }}
                            className="p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-all border border-emerald-100 group-hover:border-emerald-200"
                            title="Impersonate partner"
                          >
                            <UserCheck className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-4 relative z-10">
                          <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100 group-hover:border-emerald-100 transition-colors">
                            <div className="text-2xl font-bold text-gray-900">{partner.candidate_count || 0}</div>
                            <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Total</div>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
                            <div className="text-2xl font-bold text-blue-600">{partner.published_count || 0}</div>
                            <div className="text-[10px] uppercase font-bold text-blue-400 tracking-wider">Published</div>
                          </div>
                          <div className="bg-emerald-50 rounded-lg p-3 text-center border border-emerald-100">
                            <div className="text-2xl font-bold text-emerald-600">{partner.approved_count || 0}</div>
                            <div className="text-[10px] uppercase font-bold text-emerald-400 tracking-wider">Approved</div>
                          </div>
                          <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
                            <div className="text-2xl font-bold text-red-600">{partner.rejected_count || 0}</div>
                            <div className="text-[10px] uppercase font-bold text-red-400 tracking-wider">Rejected</div>
                          </div>
                        </div>
                        <div className="text-center relative z-10">
                          <span className="text-sm text-emerald-600 font-semibold group-hover:text-emerald-700 transition-colors flex items-center justify-center">
                            View Candidates <ChevronRight className="h-4 w-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {sourcingPartners.length === 0 && (
                    <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200 mt-4">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-20" />
                      <p>No sourcing partners found</p>
                    </div>
                  )}
                </div>
              )}

              {/* Candidates View - All or Filtered by Partner */}
              <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {showPartnerCandidates && selectedSourcingPartner
                        ? `Candidates from ${selectedSourcingPartner.name}`
                        : 'All Candidates per sourcing partner'}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1 flex items-center">
                      {showPartnerCandidates && selectedSourcingPartner
                        ? `Viewing candidates added by ${selectedSourcingPartner.name}`
                        : 'Viewing all candidates added by all sourcing partners'}
                    </p>
                    {showPartnerCandidates && selectedSourcingPartner && (
                      <button
                        onClick={handleViewAllCandidates}
                        className="mt-3 text-sm text-emerald-600 hover:text-emerald-800 font-semibold flex items-center hover:underline"
                      >
                        <ArrowLeft className="h-3 w-3 mr-1" /> View All Partners
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <select
                      value={sourcingStatusFilter}
                      onChange={(e) => {
                        setSourcingStatusFilter(e.target.value);
                        if (showPartnerCandidates && selectedSourcingPartner) {
                          fetchPartnerCandidates(selectedSourcingPartner.id);
                        } else {
                          fetchSourcingCandidates();
                        }
                      }}
                      className="appearance-none pl-4 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white text-gray-700 transition-all cursor-pointer hover:bg-gray-100"
                    >
                      <option value="all">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-50/50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Occupation</th>
                        {!showPartnerCandidates && (
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Agency</th>
                        )}
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {sourcingCandidates && sourcingCandidates.length > 0 ? (
                        sourcingCandidates.map((candidate) => (
                          <tr key={candidate.id} className={`hover:bg-gray-50/50 transition-colors ${candidate.is_new ? 'bg-amber-50/30' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              <div className="flex items-center space-x-2">
                                <span>{candidate.name}</span>
                                {candidate.is_new && (
                                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded uppercase tracking-wide border border-amber-200">
                                    NEW
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">{candidate.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {candidate.occupation ? (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                                  {candidate.occupation}
                                </span>
                              ) : <span className="text-gray-400 text-xs italic">Not specified</span>}
                            </td>
                            {!showPartnerCandidates && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex flex-col">
                                  <span className="font-medium text-gray-900">{candidate.sourcing_partner_name}</span>
                                  <span className="text-xs text-gray-400">{candidate.sourcing_partner_email}</span>
                                </div>
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2.5 py-1 text-xs font-bold rounded-full border ${candidate.sourcing_status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                candidate.sourcing_status === 'rejected' ? 'bg-red-50 text-red-700 border-red-100' :
                                  candidate.sourcing_status === 'published' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                    'bg-gray-100 text-gray-600 border-gray-200'
                                }`}>
                                {candidate.sourcing_status ? candidate.sourcing_status.charAt(0).toUpperCase() + candidate.sourcing_status.slice(1) : 'Draft'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                              <button
                                onClick={() => {
                                  setCandidateToAddToPool(candidate);
                                  setShowAddToPoolModal(true);
                                }}
                                className="p-1.5 bg-white text-blue-600 border border-gray-200 rounded-lg hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 transition-all shadow-sm"
                                title="Add to Pool"
                              >
                                <Database className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleViewUser(candidate)}
                                className="p-1.5 bg-white text-gray-500 border border-gray-200 rounded-lg hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition-all shadow-sm"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {candidate.sourcing_status === 'published' && (
                                <>
                                  <button
                                    onClick={async () => {
                                      await handleApproveSourcingCandidate(candidate.id);
                                      if (showPartnerCandidates && selectedSourcingPartner) {
                                        await fetchPartnerCandidates(selectedSourcingPartner.id);
                                      } else {
                                        await fetchSourcingCandidates();
                                      }
                                    }}
                                    className="p-1.5 bg-white text-emerald-500 border border-gray-200 rounded-lg hover:bg-emerald-500 hover:text-white hover:border-emerald-500 transition-all shadow-sm"
                                    title="Approve"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={async () => {
                                      const reason = prompt('Enter rejection reason (optional):');
                                      if (reason !== null) {
                                        await handleRejectSourcingCandidate(candidate.id, reason);
                                        if (showPartnerCandidates && selectedSourcingPartner) {
                                          await fetchPartnerCandidates(selectedSourcingPartner.id);
                                        } else {
                                          await fetchSourcingCandidates();
                                        }
                                      }
                                    }}
                                    className="p-1.5 bg-white text-red-500 border border-gray-200 rounded-lg hover:bg-red-500 hover:text-white hover:border-red-500 transition-all shadow-sm"
                                    title="Reject"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : null}
                    </tbody>
                  </table>
                  {(!sourcingCandidates || sourcingCandidates.length === 0) && (
                    <div className="text-center py-12 text-gray-400 bg-white">
                      <p className="text-lg font-medium mb-2">No candidates found</p>
                      <p className="text-sm opacity-60">
                        {sourcingStatusFilter !== 'all'
                          ? `No candidates with status "${sourcingStatusFilter}"`
                          : 'No candidates have been added by sourcing partners yet.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Partner Feedbacks Tab */}
          {(activeTab === 'partner-feedbacks' || activeTab === 'recruiter-feedbacks' || activeTab === 'sourcing-feedbacks') && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6">
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Partner Feedbacks & Requirements</h2>
                    <p className="text-sm text-gray-500">Track and respond to partner inquiries</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <div className="relative">
                      <select
                        value={feedbackPartnerTypeFilter}
                        onChange={(e) => {
                          setFeedbackPartnerTypeFilter(e.target.value);
                          fetchPartnerFeedbacks();
                        }}
                        className="appearance-none pl-4 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 cursor-pointer"
                      >
                        <option value="all">All Partners</option>
                        <option value="sourcing_partner">Sourcing Partners</option>
                        <option value="recruiting_partner">Employers</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                    </div>
                    <div className="relative">
                      <select
                        value={feedbackStatusFilter}
                        onChange={(e) => {
                          setFeedbackStatusFilter(e.target.value);
                          fetchPartnerFeedbacks();
                        }}
                        className="appearance-none pl-4 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 cursor-pointer"
                      >
                        <option value="all">All Status</option>
                        <option value="new">New</option>
                        <option value="read">Read</option>
                        <option value="responded">Responded</option>
                        <option value="resolved">Resolved</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-50/50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Partner</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Message</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Priority</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {partnerFeedbacks.map((feedback) => (
                        <tr
                          key={feedback.id}
                          className={`${feedback.status === 'new' ? 'bg-emerald-50/30' : 'hover:bg-gray-50/50'} cursor-pointer transition-colors`}
                          onClick={() => handleViewFeedback(feedback)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-gray-900">{feedback.partner_name}</div>
                            <div className="text-xs text-gray-500">{feedback.partner_email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${feedback.partner_type === 'sourcing_partner' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                              }`}>
                              {feedback.partner_type === 'sourcing_partner' ? 'Sourcing' : 'Recruiting'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-700 max-w-md truncate font-medium">{feedback.message || (feedback.feedback_type === 'request' ? 'Request' : 'Feedback')}</div>
                            {feedback.response_count > 0 && (
                              <div className="text-[10px] uppercase font-bold text-emerald-600 mt-1 flex items-center">
                                <MessageSquare className="h-3 w-3 mr-1" /> {feedback.response_count} response(s)
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-bold rounded-full uppercase tracking-wide border ${feedback.priority === 'urgent' ? 'bg-red-50 text-red-700 border-red-100' :
                              feedback.priority === 'high' ? 'bg-orange-50 text-orange-700 border-orange-100' :
                                feedback.priority === 'normal' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                  'bg-gray-50 text-gray-600 border-gray-200'
                              }`}>
                              {feedback.priority}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2.5 py-1 text-xs font-bold rounded-full border ${feedback.status === 'new' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                              feedback.status === 'read' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                feedback.status === 'responded' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                  'bg-gray-50 text-gray-600 border-gray-200'
                              }`}>
                              {feedback.status ? feedback.status.charAt(0).toUpperCase() + feedback.status.slice(1) : 'New'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(feedback.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleViewFeedback(feedback)}
                              className="p-1.5 bg-white text-gray-500 border border-gray-200 rounded-lg hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition-all shadow-sm"
                              title="View & Respond"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {feedback.status !== 'resolved' && (
                              <button
                                onClick={() => handleUpdateFeedbackStatus(feedback.id, 'resolved')}
                                className="p-1.5 bg-white text-blue-500 border border-gray-200 rounded-lg hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all shadow-sm"
                                title="Mark as Resolved"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {partnerFeedbacks.length === 0 && (
                    <div className="text-center py-12 text-gray-400 bg-white">
                      <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-20" />
                      <p>No feedbacks found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Manage Recruiter Interests Tab */}
          {activeTab === 'recruiter-interests' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6">
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Recruiter Interests</h2>
                    <p className="text-sm text-gray-500">Track and manage candidate interest from recruiters</p>
                  </div>

                  {/* Filters */}
                  <div className="flex flex-wrap gap-3">
                    <div>
                      <select
                        value={interestStatusFilter}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          setInterestStatusFilter(newStatus);
                          fetchRecruiterInterests(newStatus, interestTypeFilter);
                        }}
                        className="appearance-none pl-4 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 cursor-pointer text-gray-700"
                      >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="acked">Acked</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    <div>
                      <select
                        value={interestTypeFilter}
                        onChange={(e) => {
                          const newType = e.target.value;
                          setInterestTypeFilter(newType);
                          fetchRecruiterInterests(interestStatusFilter, newType);
                        }}
                        className="appearance-none pl-4 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 cursor-pointer text-gray-700"
                      >
                        <option value="all">All Types</option>
                        <option value="interest">Individual Interest</option>
                        <option value="pool_interest">Pool Interest</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Interests List */}
                <div className="space-y-4">
                  {recruiterInterests.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <Mail className="h-12 w-12 mx-auto mb-2 opacity-20" />
                      <p>No recruiter interests found</p>
                    </div>
                  ) : (
                    recruiterInterests.map((interest) => (
                      <div
                        key={interest.id}
                        className={`bg-white rounded-xl shadow-sm border p-6 transition-all hover:shadow-md ${interest.status === 'closed' ? 'bg-gray-50 border-gray-200 opacity-75' :
                          interest.status === 'acked' ? 'bg-emerald-50/20 border-emerald-200' :
                            'border-gray-100 hover:border-emerald-200'
                          }`}
                      >
                        <div className="flex flex-col md:flex-row justify-between items-start gap-6">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${interest.type === 'pool_interest'
                                ? 'bg-purple-50 text-purple-700 border-purple-100'
                                : 'bg-blue-50 text-blue-700 border-blue-100'
                                }`}>
                                {interest.type === 'pool_interest' ? 'Pool Interest' : 'Individual Interest'}
                              </span>
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${interest.status === 'closed'
                                ? 'bg-gray-100 text-gray-600 border-gray-200'
                                : interest.status === 'acked'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                  : 'bg-amber-50 text-amber-700 border-amber-100'
                                }`}>
                                {interest.status === 'closed' ? 'Closed' :
                                  interest.status === 'acked' ? 'Acked' : 'Pending'}
                              </span>
                              <span className="text-xs text-gray-400 flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                {new Date(interest.created_at).toLocaleString()}
                              </span>
                            </div>

                            <div className="flex items-start gap-3 mb-4">
                              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <Briefcase className="h-5 w-5 text-gray-500" />
                              </div>
                              <div>
                                <h3 className="text-base font-bold text-gray-900">{interest.recruiter_name || 'Unknown Recruiter'}</h3>
                                <p className="text-sm text-gray-500">Hidden for privacy</p>
                              </div>
                            </div>

                            {interest.pool_name && (
                              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100 inline-block">
                                <p className="text-sm text-purple-900 font-medium">
                                  Interested in Pool: <span className="font-bold">{interest.pool_name}</span>
                                </p>
                              </div>
                            )}

                            {interest.message && (
                              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 border border-gray-100 relative">
                                <MessageSquare className="h-4 w-4 text-gray-400 absolute top-4 left-4" />
                                <p className="pl-6 italic">"{interest.message}"</p>
                              </div>
                            )}

                            <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-500">
                              {interest.acked_by_name && (
                                <div className="flex items-center text-emerald-600 bg-emerald-50 px-2 py-1 rounded border border-emerald-100">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  <span>Acked by {interest.acked_by_name} on {new Date(interest.acked_at).toLocaleDateString()}</span>
                                </div>
                              )}
                              {interest.closed_by_name && (
                                <div className="flex items-center text-gray-600 bg-gray-100 px-2 py-1 rounded border border-gray-200">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  <span>Closed by {interest.closed_by_name} on {new Date(interest.closed_at).toLocaleDateString()}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Candidates Information */}
                        {interest.candidates && interest.candidates.length > 0 && (
                          <div className="mt-6 pt-6 border-t border-gray-100">
                            <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                              <Users className="h-4 w-4 mr-2 text-emerald-500" />
                              Candidates of Interest ({interest.candidates.length})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {interest.candidates.map((candidate) => (
                                <div key={candidate.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-emerald-200 transition-colors">
                                  <div className="relative">
                                    {candidate.photo_url ? (
                                      <img
                                        src={getPhotoUrl(candidate.photo_url, candidate.updated_at)}
                                        alt={candidate.name}
                                        className="h-10 w-10 rounded-full object-cover border border-gray-200"
                                      />
                                    ) : (
                                      <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                                        <Users className="h-5 w-5 text-emerald-600" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm text-gray-900 truncate">{candidate.name}</p>
                                    {candidate.occupation ? (
                                      <p className="text-xs text-gray-500 truncate">{candidate.occupation}</p>
                                    ) : <p className="text-xs text-gray-400 italic">No occupation</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {interest.candidate && (
                          <div className="mt-6 pt-6 border-t border-gray-100">
                            <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                              <User className="h-4 w-4 mr-2 text-emerald-500" />
                              Candidate of Interest
                            </h3>
                            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                              {interest.candidate.photo_url ? (
                                <img
                                  src={getPhotoUrl(interest.candidate.photo_url, interest.candidate.updated_at)}
                                  alt={interest.candidate.name}
                                  className="h-12 w-12 rounded-full object-cover border-2 border-white shadow-sm"
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                                  <User className="h-6 w-6" />
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="font-bold text-gray-900">{interest.candidate.name}</p>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {interest.candidate.occupation && (
                                    <span className="text-xs px-2 py-0.5 bg-white border border-gray-200 rounded text-gray-600">
                                      {interest.candidate.occupation}
                                    </span>
                                  )}
                                  {interest.candidate.german_level && (
                                    <span className="text-xs px-2 py-0.5 bg-white border border-gray-200 rounded text-gray-600">
                                      🇩🇪 {interest.candidate.german_level}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Request Candidates View */}

        </main>
      </div>

      {/* User Details Modal - Unified View & Edit */}
      {
        showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scaleIn border border-slate-200">
              <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-white">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Candidate Profile</h2>
                  <p className="text-slate-500 text-sm mt-1">Review and manage profile for <span className="font-bold text-emerald-600">{selectedUser.name}</span></p>
                </div>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden">
                <CandidateProfileView
                  candidate={selectedUser}
                  embedded={true}
                  onClose={() => setShowUserModal(false)}
                  onSaved={() => {
                    handleEditUser(null, false);
                  }}
                  onAction={(action, data) => {
                    if (action === 'refresh') handleEditUser(null, false);
                    if (action === 'message') {
                      setShowUserModal(false);
                      navigate('/messages');
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )
      }
      {/* Create User Modal */}
      {

        showCreateModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 border border-gray-100 animate-scaleIn">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Create New User</h2>
                  <p className="text-sm text-gray-500 mt-1">Add a new user to the system manually</p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateUserForm({
                      name: '',
                      email: '',
                      password: '',
                      role: 'candidate',
                      approval_status: 'approved',
                      profile_status: 'incomplete'
                    });
                  }}
                  className="p-2 bg-gray-50 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={createUserForm.name}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 focus:bg-white transition-all outline-none text-gray-900"
                      placeholder="e.g. John Doe"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      value={createUserForm.email}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 focus:bg-white transition-all outline-none text-gray-900"
                      placeholder="e.g. john@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="password"
                      value={createUserForm.password}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 focus:bg-white transition-all outline-none text-gray-900"
                      placeholder="••••••••"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Role *</label>
                    <div className="relative">
                      <select
                        value={createUserForm.role}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, role: e.target.value })}
                        className="w-full pl-4 pr-10 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 focus:bg-white text-gray-900 transition-all appearance-none cursor-pointer outline-none"
                        required
                      >
                        <option value="candidate">Candidate</option>
                        <option value="staff">Staff</option>
                        <option value="recruiting_partner">Employer</option>
                        <option value="sourcing_partner">Sourcing Partner</option>
                      </select>
                      <ChevronDown className="absolute right-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                    <div className="relative">
                      <select
                        value={createUserForm.approval_status}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, approval_status: e.target.value })}
                        className="w-full pl-4 pr-10 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 focus:bg-white text-gray-900 transition-all appearance-none cursor-pointer outline-none"
                      >
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <ChevronDown className="absolute right-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreateUserForm({
                        name: '',
                        email: '',
                        password: '',
                        role: 'candidate',
                        approval_status: 'approved',
                        profile_status: 'incomplete'
                      });
                    }}
                    className="px-6 py-2.5 border border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all transform hover:-translate-y-0.5"
                  >
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Password Reset Modal */}
      {
        showPasswordModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 border border-gray-100 animate-scaleIn">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {passwordResetUserId ? 'Reset User Password' : 'Reset Password'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {passwordResetUserId ? 'Set a new password for this user' : 'Update your password'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                    setPasswordResetUserId(null);
                  }}
                  className="p-2 bg-gray-50 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {passwordResetUserId && selectedUser && (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-start space-x-3">
                  <div className="bg-blue-100 p-2 rounded-full text-blue-600">
                    <User className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-blue-900">Target Account</h4>
                    <p className="text-sm text-blue-700 mt-0.5">
                      <strong>{selectedUser.name}</strong> • {selectedUser.email}
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-11 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 focus:bg-white transition-all outline-none text-gray-900"
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-11 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 focus:bg-white transition-all outline-none text-gray-900"
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {passwordError && (
                  <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-start space-x-3 text-red-700 text-sm">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <p>{passwordError}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                      setPasswordResetUserId(null);
                    }}
                    className="px-6 py-2.5 border border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all transform hover:-translate-y-0.5"
                  >
                    Reset Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Edit User Modal - Comprehensive Profile Editor */}
      {
        showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md flex items-center justify-center z-[70] p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full h-[90vh] flex flex-col overflow-hidden animate-scaleIn">
              <div className="bg-white border-b border-gray-200 px-8 py-5 flex justify-between items-center bg-opacity-95 backdrop-blur z-10">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Settings className="h-6 w-6 text-emerald-600" />
                    Edit User Profile
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">Modification mode using advanced editor</p>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                  }}
                  className="p-2 bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden bg-gray-50">
                <div className="h-full overflow-y-auto p-6 md:p-8">
                  <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                    <ProfileEditor
                      user={selectedUser}
                      onClose={() => {
                        setShowEditModal(false);
                      }}
                      onSaved={handleEditUser}
                      role="admin"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Send Message Modal */}
      <SendMessageModal
        isOpen={showMessageModal}
        onClose={() => {
          setShowMessageModal(false);
          setMessageRecipient(null);
        }}
        student={messageRecipient}
        onSent={() => {
          // Optionally refresh data or show success message
        }}
      />

      {/* Feedback Modal for Partner Feedbacks */}
      {
        showFeedbackModal && selectedFeedback && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-emerald-100 animate-scaleIn">
              {/* Modal Header */}
              <div className="bg-white border-b border-gray-100 px-8 py-6 flex justify-between items-center sticky top-0 z-10">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <MessageSquare className="h-6 w-6 text-emerald-600" />
                    Feedback Thread
                  </h2>
                  <p className="text-sm text-gray-500 mt-1 flex items-center">
                    <User className="h-3 w-3 mr-1" />
                    {selectedFeedback.partner_name}
                    <span className="mx-2 text-gray-300">|</span>
                    <Mail className="h-3 w-3 mr-1" />
                    {selectedFeedback.partner_email}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowFeedbackModal(false);
                    setSelectedFeedback(null);
                    setFeedbackResponses([]);
                    setResponseText('');
                  }}
                  className="p-2 bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-8 overflow-y-auto flex-1 bg-gray-50/50">
                <div className="max-w-2xl mx-auto space-y-6">
                  {/* Status Bar */}
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wide border ${selectedFeedback.priority === 'urgent' ? 'bg-red-50 text-red-700 border-red-100' :
                        selectedFeedback.priority === 'high' ? 'bg-orange-50 text-orange-700 border-orange-100' :
                          selectedFeedback.priority === 'normal' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                            'bg-gray-50 text-gray-700 border-gray-100'
                        }`}>
                        {selectedFeedback.priority}
                      </span>
                      <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wide border ${selectedFeedback.status === 'new' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                        selectedFeedback.status === 'read' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                          selectedFeedback.status === 'responded' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                            selectedFeedback.status === 'resolved' ? 'bg-gray-100 text-gray-700 border-gray-200' :
                              'bg-gray-50 text-gray-700 border-gray-100'
                        }`}>
                        {selectedFeedback.status}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 font-medium">
                      ID: #{selectedFeedback.id}
                    </span>
                  </div>

                  {/* Original Message */}
                  <div className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 text-emerald-600 shadow-sm border border-emerald-200">
                      <User className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="bg-white p-6 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 relative group hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-bold text-gray-900">{selectedFeedback.partner_name}</h4>
                          <span className="text-xs text-gray-400">{new Date(selectedFeedback.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedFeedback.message}</p>
                      </div>
                    </div>
                  </div>

                  {/* Responses */}
                  {feedbackResponses.length > 0 && (
                    <div className="space-y-6 pt-4 relative before:absolute before:inset-0 before:ml-9 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
                      {feedbackResponses.map((response) => (
                        <div key={response.id} className="flex gap-4 flex-row-reverse relative z-10">
                          <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 text-white shadow-md border border-blue-500">
                            <Shield className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <div className="bg-blue-50 p-6 rounded-2xl rounded-tr-none shadow-sm border border-blue-100 relative group hover:shadow-md transition-shadow">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex flex-col">
                                  <span className="font-bold text-blue-900">{response.responder_name}</span>
                                  <span className="text-[10px] uppercase font-bold text-blue-400 tracking-wider">Admin Response</span>
                                </div>
                                <span className="text-xs text-blue-400">{new Date(response.created_at).toLocaleString()}</span>
                              </div>
                              <p className="text-blue-900 whitespace-pre-wrap leading-relaxed">{response.response_text}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Response Form */}
                <div className="mt-8 bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide flex items-center">
                    <Send className="h-4 w-4 mr-2 text-emerald-500" />
                    Post a Reply
                  </h3>
                  {selectedFeedback.status !== 'resolved' ? (
                    <>
                      <textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Type your official response here..."
                        rows="4"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none mb-4 bg-gray-50 focus:bg-white transition-all outline-none text-gray-700"
                      />
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => {
                            setShowFeedbackModal(false);
                            setSelectedFeedback(null);
                            setFeedbackResponses([]);
                            setResponseText('');
                          }}
                          className="px-6 py-2.5 border border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors"
                        >
                          Close Window
                        </button>
                        <button
                          onClick={handleRespondToFeedback}
                          className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all transform hover:-translate-y-0.5 flex items-center space-x-2"
                        >
                          <Send className="h-4 w-4" />
                          <span>Send Response</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-8 text-center">
                      <div className="inline-flex items-center justify-center p-3 bg-emerald-100 rounded-full text-emerald-600 mb-4">
                        <CheckCircle className="h-8 w-8" />
                      </div>
                      <p className="text-emerald-900 text-lg font-bold">This feedback has been resolved</p>
                      <p className="text-emerald-600 mt-1 mb-6">No further responses are allowed on this thread.</p>
                      <button
                        onClick={() => {
                          setShowFeedbackModal(false);
                          setSelectedFeedback(null);
                          setFeedbackResponses([]);
                          setResponseText('');
                        }}
                        className="px-8 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-all shadow-lg"
                      >
                        Close Thread
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }
      {/* Create Pool Modal */}
      {
        showCreatePoolModal && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 animate-scaleIn border border-slate-200">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-slate-800 text-left">Create New Talent Pool</h2>
                <button onClick={() => setShowCreatePoolModal(false)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2 text-left">Pool Name</label>
                  <input
                    type="text"
                    value={newPoolName}
                    onChange={(e) => setNewPoolName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none font-medium text-left text-gray-900"
                    placeholder="e.g., German Speaking Nurses"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowCreatePoolModal(false)}
                    className="flex-1 py-3 border-2 border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreatePool}
                    className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-bold shadow-lg shadow-emerald-500/30 hover:bg-emerald-700 transition-all"
                  >
                    Create Pool
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Pool Candidates Modal */}
      {
        showPoolCandidatesModal && selectedPool && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col animate-scaleIn border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                <div className="text-left">
                  <h2 className="text-2xl font-bold text-slate-800">{selectedPool.name}</h2>
                  <p className="text-sm text-slate-500">Candidates in this pool</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowSelectCandidateModal(true)}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Add Candidates
                  </button>
                  <button onClick={() => setShowPoolCandidatesModal(false)} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 admin-invisible-scrollbar text-left">
                {poolCandidates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                    <Users className="h-16 w-16 mb-4 opacity-20" />
                    <p className="text-lg font-medium">No candidates in this pool yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {poolCandidates.map(candidate => (
                      <div key={candidate.id} className="flex items-center gap-4 p-4 rounded-2xl border border-slate-100 bg-white hover:border-emerald-200 hover:shadow-md transition-all group">
                        <div className="h-14 w-14 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0 border border-slate-200">
                          <img
                            src={getPhotoUrl(candidate.photo_url, candidate.updated_at) || `https://ui-avatars.com/api/?name=${encodeURIComponent(candidate.name)}&background=random`}
                            alt={candidate.name}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-slate-800 truncate">{candidate.name}</h3>
                          <p className="text-xs text-slate-500 truncate">{candidate.occupation || 'Candidate'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${candidate.german_level ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-400'
                              }`}>
                              {candidate.german_level || 'No German Level'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setShowPoolCandidatesModal(false);
                              handleViewUser(candidate);
                              markCandidateAsViewed(candidate);
                            }}
                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                            title="View Profile"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleRemoveFromPool(candidate.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                            title="Remove from Pool"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Select Candidate For Pool Modal (Reverse Flow) */}
      {
        showSelectCandidateModal && selectedPool && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col animate-scaleIn border border-slate-200">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Add Candidates to Pool</h2>
                  <p className="text-sm text-slate-500">Adding to: {selectedPool.name}</p>
                </div>
                <button onClick={() => setShowSelectCandidateModal(false)} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search candidates by name or email..."
                    value={candidateSearchQuery}
                    onChange={(e) => setCandidateSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none font-medium text-gray-900"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 admin-invisible-scrollbar">
                <div className="space-y-2">
                  {users.filter(u =>
                    u.role === 'candidate' &&
                    u.approval_status === 'approved' &&
                    !poolCandidates.some(pc => pc.id === u.id) &&
                    (u.name?.toLowerCase().includes(candidateSearchQuery.toLowerCase()) ||
                      u.email?.toLowerCase().includes(candidateSearchQuery.toLowerCase()))
                  ).length === 0 ? (
                    <p className="text-center py-8 text-slate-500 italic">No available candidates found matching your search.</p>
                  ) : (
                    users.filter(u =>
                      u.role === 'candidate' &&
                      u.approval_status === 'approved' &&
                      !poolCandidates.some(pc => pc.id === u.id) &&
                      (u.name?.toLowerCase().includes(candidateSearchQuery.toLowerCase()) ||
                        u.email?.toLowerCase().includes(candidateSearchQuery.toLowerCase()))
                    ).map(candidate => (
                      <div key={candidate.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all group">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold overflow-hidden border border-slate-200">
                            {candidate.photo_url ? (
                              <img src={getPhotoUrl(candidate.photo_url, candidate.updated_at)} alt={candidate.name} className="h-full w-full object-cover" />
                            ) : (
                              candidate.name.charAt(0).toUpperCase()
                            )}
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-800 text-sm">{candidate.name}</h4>
                            <p className="text-xs text-slate-500">{candidate.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddCandidateToPool(candidate)}
                          className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-bold hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all shadow-sm"
                        >
                          Add
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Add To Pool Modal */}
      {
        showAddToPoolModal && candidateToAddToPool && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[80] p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 animate-scaleIn border border-slate-200">
              <div className="flex justify-between items-center mb-6">
                <div className="text-left">
                  <h2 className="text-xl font-bold text-slate-800">Add to Pool</h2>
                  <p className="text-sm text-gray-500">Select a pool for {candidateToAddToPool.name}</p>
                </div>
                <button onClick={() => { setShowAddToPoolModal(false); setCandidateToAddToPool(null); }} className="p-2 hover:bg-slate-100 rounded-full text-slate-400">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-3 max-h-64 overflow-y-auto pr-2 admin-invisible-scrollbar text-left">
                {manualPools.length === 0 ? (
                  <p className="text-gray-500 italic py-4">No pools created yet. Please create a pool in the Overview tab first.</p>
                ) : (
                  manualPools.map(pool => (
                    <button
                      key={pool.id}
                      onClick={() => handleAddToPool(pool.id)}
                      className="w-full p-4 flex items-center justify-between rounded-xl border border-gray-100 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all font-bold text-gray-700"
                    >
                      <div className="flex items-center gap-3">
                        <Database className="h-5 w-5 text-emerald-500" />
                        <span>{pool.name}</span>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-lg">
                        {pool.candidate_count}
                      </span>
                    </button>
                  ))
                )}
              </div>

              <div className="mt-8 pt-6 border-t border-gray-100">
                <button
                  onClick={() => { setShowAddToPoolModal(false); setCandidateToAddToPool(null); }}
                  className="w-full py-3 border-2 border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Occupation Statistics Modal */}
      {
        showOccupationModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden animate-scaleIn">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">{selectedOccupationStats}</h2>
                  <p className="text-sm text-slate-500">Candidate Breakdown</p>
                </div>
                <button
                  onClick={() => setShowOccupationModal(false)}
                  className="p-2 hover:bg-slate-200 rounded-full transition-colors"
                >
                  <X className="h-6 w-6 text-slate-500" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-bold text-slate-700">Candidates in this role: {occupationCandidates.length}</p>
                </div>

                <div className="space-y-3">
                  {occupationCandidates.length === 0 ? (
                    <p className="text-center py-8 text-slate-400">No candidates found for this occupation.</p>
                  ) : (
                    occupationCandidates.map(candidate => (
                      <div key={candidate.id} className="p-4 border border-slate-200 rounded-xl hover:bg-blue-50/30 transition-colors flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600">
                            {candidate.photo_url ? (
                              <img src={getPhotoUrl(candidate.photo_url, candidate.updated_at)} alt={candidate.name} className="h-full w-full object-cover rounded-full" />
                            ) : (
                              candidate.name.charAt(0)
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-slate-800">{candidate.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${candidate.german_level ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-400'
                                }`}>
                                {candidate.german_level || 'No German Level'}
                              </span>
                              <span className="text-[10px] text-slate-400">|</span>
                              <span className="text-[10px] text-slate-500">{candidate.experience || 'No experience'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewUser(candidate)}
                            className="px-3 py-1.5 text-sm bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 font-medium"
                          >
                            View Profile
                          </button>
                          <button
                            onClick={() => {
                              setCandidateToAddToPool(candidate);
                              setShowAddToPoolModal(true);
                            }}
                            className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium"
                          >
                            Add to Pool
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                <button
                  onClick={() => setShowOccupationModal(false)}
                  className="px-6 py-2 bg-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Reject Request Modal */}
      {showRejectRequestModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Decline Request</h3>
            <p className="text-sm text-gray-500 mb-6">
              Please provide a reason for declining this request. This will be visible to the recruiter.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Decline</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full h-32 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500 transition-all text-sm resize-none text-gray-900"
                  placeholder="e.g. Candidates not available for these requirements..."
                  disabled={rejectingRequest}
                />
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectRequestModal(false);
                  setRequestToReject(null);
                  setRejectReason('');
                }}
                disabled={rejectingRequest}
                className="px-4 py-2 bg-white text-gray-700 font-medium text-sm rounded-xl hover:bg-gray-50 transition-colors border border-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectRequest}
                disabled={!rejectReason.trim() || rejectingRequest}
                className="px-4 py-2 bg-red-600 text-white font-medium text-sm rounded-xl hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
              >
                {rejectingRequest ? 'Declining...' : 'Confirm Decline'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ScheduleInterviewModal
        isOpen={showScheduleInterviewModal}
        onClose={() => setShowScheduleInterviewModal(false)}
        onSchedule={handleScheduleInterview}
        candidate={interviewCandidate || {}}
        currentUser={currentUser}
      />

    </div >
  );
};

export default AdminDashboard;
